import ResearchTiming from '../../MyResearch/ResearchTiming';
import React, { useEffect, useState } from 'react';
import TradingViewChart from '../Report/TradingViewChart_new';
import Chart from 'react-apexcharts';
import { Container, Row, Col } from 'reactstrap';
import { useParams, Link, useLocation } from 'react-router-dom';
import PromptGroupTable from './PromptGroupTable';
import SettleModal from '../../MyResearch/SettleModal';
import { SelfReportLine } from './SelfReport';
import { displayModelName } from '../../../helpers/modelName';
import ResearchDisclaimer from '../../../Components/Common/ResearchDisclaimer';
import formatHorizon from './formatHorizon';
import buildPineScript, { buildComparePineScript } from './pineScript';
import axios from 'axios';
import { getLoggedinUser, getAuthHeaders } from '../../../helpers/api_helper';
import { setResearchVisibility, updatePredictionBlogger } from '../../../services/userPredictionService';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/*
 * A bare axios, deliberately not the shared one.
 *
 * api_helper installs a global response interceptor that rejects with a plain
 * STRING -- Promise.reject(message) -- which throws away the response. That is
 * fine for a form that only wants a sentence to show, but this page has to tell
 * a private forecast from a missing one and a missing one from a server fault,
 * and the status code and the error code are exactly what the interceptor
 * discards. A private forecast reached the catch block as the string
 * "private_forecast", with err.response undefined, so every branch fell through
 * to "something went wrong on our side".
 *
 * Interceptors registered on the default instance do not apply to instances
 * made with create(), so this gets the real response. The token is attached per
 * request via getAuthHeaders() rather than inherited: create() copies
 * axios.defaults once, at module load, which is before sign-in has necessarily
 * happened.
 */
const rawApi = axios.create();

export default function PredictionDetailPage() {
  const { predictionId } = useParams();
  // Detect clean/embed mode from URL (match Report page behaviour)
  const computeIsClean = () => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const clean = (sp.get('clean') || '').toString().toLowerCase();
      const ui = (sp.get('ui') || '').toString().toLowerCase();
      const embed = (sp.get('embed') || '').toString().toLowerCase();
      const bySearch = clean === '1' || clean === 'true' || ui === 'clean' || ui === 'embed' || embed === '1' || embed === 'true';
      if (bySearch) return true;
      // fallback: check the full href/path for malformed appended params like /prediction/72809&clean=true
      const href = (window.location.href || '').toString().toLowerCase();
      const m = href.match(/(?:[?&/])(clean|ui|embed)=([^&#/]+)/i);
      if (!m) return false;
      const key = (m[1] || '').toLowerCase();
      const val = (m[2] || '').toLowerCase();
      if (key === 'clean' || key === 'embed') return val === '1' || val === 'true';
      if (key === 'ui') return val === 'clean' || val === 'embed';
      return false;
    } catch {
      return false;
    }
  };

  const isCleanMode = React.useMemo(() => computeIsClean(), []);
  // Detect session parameter in URL
  const hasSessionParam = React.useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      return !!sp.get('session');
    } catch {
      return false;
    }
  }, []);
  // showreason parameter to explicitly reveal prediction reason when in clean mode
  const showReasonParam = React.useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      return (sp.get('showreason') || '').toString().toLowerCase() === 'true';
    } catch {
      return false;
    }
  }, []);
  // Detect a vertical mode parameter (vertical=1/true) - tolerant parsing like computeIsClean
  const computeIsVertical = () => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const v = (sp.get('vertical') || sp.get('vertical') || '').toString().toLowerCase();
      if (v === '1' || v === 'true') return true;
      const href = (window.location.href || '').toString().toLowerCase();
      const m = href.match(/(?:[?&/])(vertical)=([^&#/]+)/i);
      if (!m) return false;
      const val = (m[2] || '').toLowerCase();
      return val === '1' || val === 'true';
    } catch { return false; }
  };
  const isVertical = React.useMemo(() => computeIsVertical(), []);

  // Detect showcandles parameter (showcandles=1/true)
  // Default behavior: if the parameter is NOT present at all, assume true
  // (show candles by default on PredictionDetail). If parameter is present
  // the explicit value wins. We still support malformed appended params via
  // the href fallback.
  const computeIsShowCandles = () => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      // If param explicitly provided, respect it
      if (sp.has('showcandles') || sp.has('showCandles')) {
        const v = (sp.get('showcandles') || sp.get('showCandles') || '').toString().toLowerCase();
        return v === '1' || v === 'true';
      }
      // fallback: check the full href/path for malformed appended params like /prediction/72809&showcandles=true
      const href = (window.location.href || '').toString().toLowerCase();
      const m = href.match(/(?:[?&/])(showcandles|showCandles)=([^&#/]+)/i);
      if (m) {
        const val = (m[2] || '').toLowerCase();
        return val === '1' || val === 'true';
      }
      // No explicit parameter found -> default to true
      return true;
    } catch { return true; }
  };
  const isShowCandles = React.useMemo(() => computeIsShowCandles(), []);
  
  // Detect ChartOnly parameter (chartonly=1/true) — if true we'll render only the chart
  const computeIsChartOnly = () => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const val = (sp.get('chartonly') || sp.get('chartOnly') || '').toString().toLowerCase();
      if (val === '1' || val === 'true') return true;
      // fallback: check the full href/path for malformed appended params like /prediction/72809&chartonly=true
      const href = (window.location.href || '').toString().toLowerCase();
      const m = href.match(/(?:[?&/])(chartonly|chartOnly)=([^&#/]+)/i);
      if (!m) return false;
      const v = (m[2] || '').toLowerCase();
      return v === '1' || v === 'true';
    } catch (e) { return false; }
  };
  const isChartOnly = React.useMemo(() => computeIsChartOnly(), []);

  // /prediction/:id/response -- the same page, with the reasoning in the slot
  // the chart occupies.
  //
  // This is the whole of that route. It was briefly its own component, which
  // meant a second copy of the heading, the timestamps and the comparison
  // table, each free to drift from the originals; and it dropped everything it
  // had not been given a copy of, so the page arrived at looked like a
  // different forecast from the page linked from. One page with a swapped panel
  // cannot disagree with itself.
  const { pathname } = useLocation();
  const isReasonMode = /\/response\/?$/.test(pathname || '');
  
  // Detect backtest parameter (backtest=0/false or backtest=1/true) — default false
  const computeIsBacktest = () => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const val = (sp.get('backtest') || sp.get('Backtest') || '').toString().toLowerCase();
      if (val === '1' || val === 'true') return true;
      // fallback: check the full href/path for malformed appended params
      const href = (window.location.href || '').toString().toLowerCase();
      const m = href.match(/(?:[?&/])(backtest|Backtest)=([^&#/]+)/i);
      if (m) {
        const v = (m[2] || '').toLowerCase();
        return v === '1' || v === 'true';
      }
      return false;
    } catch { return false; }
  };
  const isBacktest = React.useMemo(() => computeIsBacktest(), []);
  
  // Detect days parameter for backtest mode (days=3,7,30, etc) — no default
  const computeBacktestDays = () => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const val = sp.get('days') || '';
      if (val) {
        const n = parseInt(val, 10);
        return isFinite(n) && n > 0 ? n : null;
      }
      // fallback: check the full href/path for malformed appended params
      const href = window.location.href || '';
      const m = href.match(/(?:[?&/])days=([^&#/]+)/i);
      if (m) {
        const n = parseInt(m[1], 10);
        return isFinite(n) && n > 0 ? n : null;
      }
      return null;
    } catch { return null; }
  };
  const backtestDays = React.useMemo(() => computeBacktestDays(), []);
  // Detect drawprice parameter (drawprice=true/false) — default true
  const computeIsDrawPrice = () => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const val = (sp.get('drawprice') || sp.get('drawPrice') || '').toString().toLowerCase();
      if (val === '0' || val === 'false') return false;
      return true; // default true
    } catch { return true; }
  };
  const [isDrawPrice, setIsDrawPrice] = useState(() => computeIsDrawPrice());
  // Detect showtable parameter (showtable=true/false) — default false
  const computeIsShowTable = () => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const val = (sp.get('showtable') || sp.get('showTable') || '').toString().toLowerCase();
      if (val === '1' || val === 'true') return true;
      return false; // default false
    } catch { return false; }
  };
  const [isShowTable, setIsShowTable] = useState(() => computeIsShowTable());
  // Detect cashstrategy URL param (value × 0.001 = decimal threshold, e.g. 5 → 0.5%)
  const computeInitialCashStrategy = () => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const val = sp.get('cashstrategy');
      if (val) { const n = parseFloat(val); if (isFinite(n) && n > 0) return n; }
      return null; // defer default until prediction timeframe is known
    } catch { return null; }
  };
  // Detect roundfee URL param (value × 0.001 = decimal fee, e.g. 2 → 0.2%)
  const computeInitialRoundFee = () => {
    try {
      const sp = new URLSearchParams(window.location.search || '');
      const val = sp.get('roundfee');
      if (val) { const n = parseFloat(val); if (isFinite(n) && n > 0) return n; }
      return 2; // default 2 = 0.2%
    } catch { return 2; }
  };
  const [showOptionsPanel, setShowOptionsPanel] = useState(() => !computeIsClean());

  // Trading fee per transaction (0.2%)
  const TRADING_FEE = 0.002;

  // Calculate suggestion based on prediction midpoint vs current price (same logic as P/L calculation)
  const calculateSuggestion = (pred, cashStrategyParam = 5) => {
    if (!pred) return { label: '-', color: '#999', direction: 0 };
    
    // Try multiple field name variations for low/high
    const predLow = pred.prediction_low ?? pred.predictionLow ?? pred.range_min ?? pred.rangeMin;
    const predHigh = pred.prediction_high ?? pred.predictionHigh ?? pred.range_max ?? pred.rangeMax;
    
    // Use == null to check for null/undefined (but allow 0)
    if (predLow == null || predHigh == null) return { label: '-', color: '#999', direction: 0 };
    
    // Get the price at prediction time
    const priceAtPred = pred.current_price ?? 
                       pred.currentPrice ?? 
                       pred.price_at_prediction ?? 
                       pred.priceAtPrediction ?? 
                       pred.price;
    
    if (!priceAtPred || priceAtPred === 0) return { label: '-', color: '#999', direction: 0 };
    
    // Parse as floats to ensure numeric comparison
    const lowNum = parseFloat(predLow);
    const highNum = parseFloat(predHigh);
    const priceNum = parseFloat(priceAtPred);
    
    if (!isFinite(lowNum) || !isFinite(highNum) || !isFinite(priceNum)) {
      return { label: '-', color: '#999', direction: 0 };
    }
    
    // Calculate prediction direction from midpoint of range (same as P/L calculation)
    const predictionMidpoint = (lowNum + highNum) / 2.0;
    const predictionChangeRatio = (predictionMidpoint - priceNum) / priceNum;
    
    // Use the dynamic cash strategy threshold (cashStrategyParam / 1000, e.g., 5 → 0.5%)
    const cashThreshold = cashStrategyParam / 1000;
    
    console.log('calculateSuggestion:', { lowNum, highNum, priceNum, midpoint: predictionMidpoint, changeRatio: predictionChangeRatio, cashThreshold });
    
    if (predictionChangeRatio > cashThreshold) {
      return { label: 'LONG', color: '#2e7d32', direction: 1 };
    } else if (predictionChangeRatio < -cashThreshold) {
      return { label: 'SHORT', color: '#c62828', direction: -1 };
    } else {
      return { label: 'HOLD', color: '#666', direction: 0 };
    }
  };

  // Check if a date string falls on Saturday (6) or Sunday (0)
  const isWeekend = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return false;
    try {
      // Extract just the date part (YYYY-MM-DD) - handle both space and T separators
      let dateOnly = dateStr;
      if (dateStr.includes(' ')) {
        dateOnly = dateStr.split(' ')[0];
      } else if (dateStr.includes('T')) {
        dateOnly = dateStr.split('T')[0];
      }
      const date = new Date(dateOnly + 'T00:00:00Z'); // Parse as UTC
      const dayOfWeek = date.getUTCDay();
      return dayOfWeek === 0 || dayOfWeek === 6; // Sunday (0) or Saturday (6)
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    if (isCleanMode) document.body.classList.add('clean-mode'); else document.body.classList.remove('clean-mode');
    return () => { document.body.classList.remove('clean-mode'); };
  }, [isCleanMode]);
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [errorCode, setErrorCode] = useState(null);
  const [errorKind, setErrorKind] = useState(null);
  // Sharing this forecast's whole prompt group, for its owner or an admin.
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [settling, setSettling] = useState(false);
  const [updatingBlogger, setUpdatingBlogger] = useState(false);
  const [bloggerUpdateMsg, setBloggerUpdateMsg] = useState(null);
  // Bumped after a settlement so the fetch below runs again. The score, the
  // outcome columns and the chart script all change at once, and each of them
  // is derived from the response rather than held separately -- patching the
  // row in place would mean recomputing the scorer in the browser.
  const [reloadKey, setReloadKey] = useState(0);
  const [showChart, setShowChart] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [reasonCopied, setReasonCopied] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  // 'one' | 'all' | null -- which script is open, if either.
  const [showPine, setShowPine] = useState(null);
  const [pineCopied, setPineCopied] = useState(false);
  const [comparePineCopied, setComparePineCopied] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  // Copy to the clipboard, flashing the button label back after a moment.
  const copyText = (text, setFlag) => {
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => {
        setFlag(true);
        setTimeout(() => setFlag(false), 2500);
      })
      .catch(() => {});
  };
  const [historicalPnL, setHistoricalPnL] = useState({
    three_day: null,
    seven_day: null,
    thirty_day: null,
    ninety_day: null
  });
  const [atrMetrics, setAtrMetrics] = useState({
    three_day: null,
    seven_day: null,
    thirty_day: null,
    ninety_day: null
  });
  const [backtestTrades, setBacktestTrades] = useState([]); // Array of trades for backtest mode
  const [backtestChartData, setBacktestChartData] = useState([]); // Chart data for P/L over time
  const [backtestPriceData, setBacktestPriceData] = useState([]); // Actual price data for dual-axis chart
  // Applied backtest params (trigger recalculation when changed via Apply button)
  const [backtestCashStrategyParam, setBacktestCashStrategyParam] = useState(() => computeInitialCashStrategy() ?? 5);
  const [backtestRoundFeeParam, setBacktestRoundFeeParam] = useState(() => computeInitialRoundFee());
  // Pending UI selections (not yet applied)
  const [pendingCashStrategyParam, setPendingCashStrategyParam] = useState(() => computeInitialCashStrategy() ?? 5);
  const [pendingRoundFeeParam, setPendingRoundFeeParam] = useState(() => computeInitialRoundFee());

  // Fetch prediction details
  useEffect(() => {
    const fetchPredictionDetails = async () => {
      if (!predictionId) {
        setErrorMsg("No prediction ID provided.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        // If the client requested showcandles, ask the backend to include the relevant OHLCV candles
        const url = `${API}/api/prediction-details/${predictionId}${isShowCandles ? '?candles=true' : ''}`;
        const res = await rawApi.get(url, { headers: getAuthHeaders() });
        const data = res?.data?.prediction ?? res?.data ?? res;
        // Debug - log if backend returned candles
        console.debug('PredictionDetail fetched', { id: predictionId, candlesCount: Array.isArray(data?.candles) ? data.candles.length : 0, hasPath: Array.isArray(data?.price_path) });
        // minimal debug to confirm shape during troubleshooting
        console.debug('PredictionDetail fetched', { id: predictionId, hasNested: !!(res && (res.prediction || res?.data?.prediction)) });
        if (data && Array.isArray(data.candles)) {
          console.debug('PredictionDetail: received candles count', data.candles.length);
        }
        setPrediction(data);
        setErrorMsg(null);
      } catch (err) {
        // Surface what the API said, not "Request failed with status code 403".
        // This page is reachable without a session, so a refusal is a normal
        // outcome -- a private forecast, or a system one the visitor has no
        // entitlement for -- and the server already words both.
        const body = err?.response?.data || err?.data || err;
        setErrorMsg(body?.message || err?.message || null);
        setErrorCode(err?.response?.status || body?.status || null);
        // 'private_forecast', 'subscription_required', or absent. The page says
        // different things for each, and prose is not something to match on.
        setErrorKind(typeof body?.error === 'string' ? body.error : null);
        setPrediction(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPredictionDetails();
  }, [predictionId, isShowCandles, reloadKey]);

  // Apply timeframe-aware default for cash strategy threshold if no URL param was provided
  useEffect(() => {
    if (!prediction) return;
    const urlOverride = computeInitialCashStrategy();
    if (urlOverride !== null) return; // URL param takes priority
    // 1h = 3 (0.3%), all others = 5 (0.5%)
    const defaultVal = (prediction.timeframe === '1h') ? 3 : 5;
    setBacktestCashStrategyParam(defaultVal);
    setPendingCashStrategyParam(defaultVal);
  }, [prediction?.timeframe]);

  // Calculate P/L for historical predictions
  const calculateHistoricalPnL = async (pred) => {
    if (!pred || !pred.model || !pred.symbol || !pred.create_date) {
      console.warn('Missing required fields for P/L calculation:', { model: pred?.model, symbol: pred?.symbol, create_date: pred?.create_date });
      setHistoricalPnL({ three_day: null, seven_day: null, thirty_day: null, ninety_day: null });
      setBacktestTrades([]);
      setBacktestChartData([]);
      setBacktestPriceData([]);
      return;
    }

    // Derive active parameters from state (updated by Apply button)
    const cashThreshold = backtestCashStrategyParam / 1000;
    const tradingFee = backtestRoundFeeParam / 1000;

    try {
      console.log('Calculating historical P/L for:', { model: pred.model, symbol: pred.symbol, create_date: pred.create_date, cashThreshold, tradingFee });
      
      const timeWindows = {
        three_day: 3,
        seven_day: 7,
        thirty_day: 30,
        ninety_day: 90
      };

      const results = {};

      // Fetch all scored predictions for this model/symbol/timeframe (no date cutoff).
      // The 3/7/30/90 windows are applied by slicing the last N records after fetch.
      const params = {
        model: pred.model,
        symbol: pred.symbol,
        period: pred.timeframe || '1d', // Use prediction's own timeframe
        include_scored: 'true', // Only scored predictions with actual_price
        limit: 200, // Last 200 scored predictions (more than enough for 90-point window)
        offset: 0
      };

      const response = await axios.get(`${API}/api/predictions`, { params });
      const apiData = response.data || response;
      let allPredictions = Array.isArray(apiData.predictions) ? apiData.predictions : [];
      // Exclude the current prediction itself
      allPredictions = allPredictions.filter(p => p.id !== pred.id);
      console.log(`Fetched ${allPredictions.length} total scored predictions for ${pred.model} ${pred.symbol} ${pred.timeframe}`);

      // First, reverse to process in chronological order (oldest first)
      // This ensures previousDirection tracking is correct when determining if a fee should be charged
      let chronologicalPredictions = [...allPredictions].sort((a, b) => {
        const aDate = a.input_time ? new Date(a.input_time) : (a.create_date ? new Date(a.create_date) : null);
        const bDate = b.input_time ? new Date(b.input_time) : (b.create_date ? new Date(b.create_date) : null);
        if (!aDate || !bDate) return 0;
        return aDate - bDate;
      });

      // --- Patch: Fetch latest prediction for last date, even if not scored ---
      // Only show previous N scored days in backtest table. Do not add last day if not scored.

      // First pass: collect all trades (including neutral predictions) for backtest
      const allValidTrades = [];
      let globalTotalReturn = 1;
      let previousDirection = 0; // Track previous direction to determine if fee should be charged

      for (const p of chronologicalPredictions) {
        const actual = (p.actual_price != null && p.actual_price !== '') ? parseFloat(p.actual_price) : null;
        const low = parseFloat(p.prediction_low);
        const high = parseFloat(p.prediction_high);
        const priceAtPred = parseFloat(p.current_price || p.currentPrice || p.price_at_prediction || p.priceAtPrediction || p.price);

        if (actual != null && isFinite(low) && isFinite(high) && isFinite(priceAtPred) && priceAtPred !== 0) {
          // Calculate prediction direction from midpoint of range
          const predictionMidpoint = (low + high) / 2.0;
          const predictionChangeRatio = (predictionMidpoint - priceAtPred) / priceAtPred;
          let predictionDirection = 0;
          if (predictionChangeRatio > cashThreshold) {
            predictionDirection = 1; // bullish
          } else if (predictionChangeRatio < -cashThreshold) {
            predictionDirection = -1; // bearish
          }

          // Calculate fee: 
          // - Always charge fee when ENTERING a new position (direction changes or from null)
          // - No fee when HOLDING the same direction across neutral periods
          // - No fee when in neutral/cash
          let feeCharged = 0;
          if (predictionDirection !== 0) {
            // Check if this is the same direction as the previous trade
            if (previousDirection === predictionDirection) {
              // Same direction = holding = no fee
              feeCharged = 0;
            } else {
              // Different direction or first trade = fee applies
              feeCharged = tradingFee;
            }
          }

          // Include ALL predictions in the table, but only those with clear direction affect cumulative return
          let dailyReturn = 0;
          if (predictionDirection !== 0) {
            dailyReturn = ((actual - priceAtPred) / priceAtPred) * predictionDirection - feeCharged;
            globalTotalReturn *= (1 + dailyReturn);
          }

          // Extract and normalize confidence from various possible fields
          const rawConf = p.confidence ?? p.prediction_confidence ?? p.predictionConfidence ?? p.prediction_confidence_pct ?? p.confidence_pct ?? p.prediction_confidence_percent ?? p.prediction_confidence_percentile ?? p.score ?? p.prediction_score;
          let confNum = null;
          if (rawConf != null) {
            const parsed = parseFloat(rawConf);
            if (isFinite(parsed)) {
              // Normalize to percentage (0-100)
              confNum = parsed <= 1 ? parsed * 100 : parsed;
            }
          }

          allValidTrades.push({
            id: p.id,
            date: p.input_time ? p.input_time : (p.create_date ? new Date(p.create_date).toISOString() : 'Unknown'),
            symbol: p.symbol || pred.symbol,
            predictionLow: low,
            predictionHigh: high,
            currentPrice: priceAtPred,
            actualPrice: actual,
            direction: predictionDirection,
            directionalReturn: predictionDirection !== 0 ? ((actual - priceAtPred) / priceAtPred) * predictionDirection : 0,
            dailyReturn: dailyReturn,
            feeCharged: feeCharged, // Store the actual fee charged
            pnl: predictionDirection !== 0 ? (((actual - priceAtPred) / priceAtPred) * predictionDirection - feeCharged) * 100 : 0,
            cumulativeReturn: globalTotalReturn,
            confidence: confNum,
            isNeutral: predictionDirection === 0, // Mark neutral predictions
            tradeAction: (predictionDirection !== 0 && feeCharged === 0) ? 'HOLD' : (predictionDirection !== 0 ? 'NEW/FLIP' : 'NEUTRAL')
          });

          // Update previous direction
          if (predictionDirection !== 0) {
            // Entering a position
            previousDirection = predictionDirection;
          } else {
            // Going to neutral/cash resets the position state
            previousDirection = 0;
          }
        }
      }

      console.log(`Collected ${allValidTrades.length} valid trades for backtest`);

      // For each time window, take the last N predictions and calculate P/L
      for (const [key, count] of Object.entries(timeWindows)) {
        // Take the last 'count' predictions from the chronological list (most recent)
        const slicedPredictions = chronologicalPredictions.slice(-count);
        
        console.log(`${key}: Using last ${slicedPredictions.length} predictions`);

        // Calculate P/L using the same formula as Report page
        let totalReturn = 1; // Start with 100% (compound returns)
        let validCount = 0;
        let windowPreviousDirection = 0; // Track previous direction within this time window

        for (const p of slicedPredictions) {
          const actual = (p.actual_price != null && p.actual_price !== '') ? parseFloat(p.actual_price) : null;
          const low = parseFloat(p.prediction_low);
          const high = parseFloat(p.prediction_high);
          const priceAtPred = parseFloat(p.current_price || p.currentPrice || p.price_at_prediction || p.priceAtPrediction || p.price);

          if (actual != null && isFinite(low) && isFinite(high) && isFinite(priceAtPred) && priceAtPred !== 0) {
            // Calculate prediction direction from midpoint of range
            const predictionMidpoint = (low + high) / 2.0;
            const predictionChangeRatio = (predictionMidpoint - priceAtPred) / priceAtPred;
            let predictionDirection = 0;
            if (predictionChangeRatio > cashThreshold) {
              predictionDirection = 1; // bullish
            } else if (predictionChangeRatio < -cashThreshold) {
              predictionDirection = -1; // bearish
            }

            // Calculate P/L only if prediction is not neutral
            if (predictionDirection !== 0) {
              // Only charge fee if direction changes (not holding same position)
              let timeWindowFee = 0;
              if (windowPreviousDirection === predictionDirection) {
                // Same direction = holding = no fee
                timeWindowFee = 0;
              } else {
                // Different direction or first trade = fee applies
                timeWindowFee = tradingFee;
              }
              const dailyReturn = ((actual - priceAtPred) / priceAtPred) * predictionDirection - timeWindowFee;
              totalReturn *= (1 + dailyReturn);
              validCount++;
              windowPreviousDirection = predictionDirection;
            } else {
              // Going to neutral/cash resets the position state
              windowPreviousDirection = 0;
            }
          }
        }


        results[key] = validCount > 0 ? totalReturn - 1 : null;
        console.log(`${key} P/L: ${results[key]} (${validCount} valid trades out of ${slicedPredictions.length})`);
      }

      console.log('Historical P/L results:', results);
      setHistoricalPnL(results);

      // Compute ATR metrics from the same predictions data (using actual_high/actual_low when available)
      // All values expressed as % of the price at each prediction day
      const atrResultsFromPreds = {};
      for (const [key, count] of Object.entries(timeWindows)) {
        const slice = chronologicalPredictions.slice(-count);
        const priceSeq = slice
          .filter(p => (p.actual_price != null && p.actual_price !== '') && isFinite(parseFloat(p.actual_price)))
          .map(p => {
            const close = parseFloat(p.actual_price);
            const high = (p.actual_high != null && p.actual_high !== '' && isFinite(parseFloat(p.actual_high)))
              ? parseFloat(p.actual_high) : close;
            const low = (p.actual_low != null && p.actual_low !== '' && isFinite(parseFloat(p.actual_low)))
              ? parseFloat(p.actual_low) : close;
            const price = isFinite(parseFloat(p.current_price)) && parseFloat(p.current_price) !== 0
              ? parseFloat(p.current_price) : close;
            return { high, low, close, price };
          });
        if (priceSeq.length < 2) { atrResultsFromPreds[key] = null; continue; }
        // True range as % of that day's price
        const trPcts = [];
        for (let i = 1; i < priceSeq.length; i++) {
          const { high: h, low: l, price } = priceSeq[i];
          const pc = priceSeq[i - 1].close;
          const tr = Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc));
          trPcts.push((tr / price) * 100);
        }
        const atrPct = trPcts.reduce((s, v) => s + v, 0) / trPcts.length;
        // Prediction range as % of current_price for each prediction in window
        const rangePcts = slice
          .filter(p => isFinite(parseFloat(p.prediction_high)) && isFinite(parseFloat(p.prediction_low)) &&
                       isFinite(parseFloat(p.current_price)) && parseFloat(p.current_price) !== 0)
          .map(p => (Math.abs(parseFloat(p.prediction_high) - parseFloat(p.prediction_low)) / parseFloat(p.current_price)) * 100);
        const predRangePct = rangePcts.length > 0
          ? rangePcts.reduce((s, v) => s + v, 0) / rangePcts.length
          : null;
        atrResultsFromPreds[key] = isFinite(atrPct)
          ? { atr: atrPct, predRange: predRangePct, ratio: atrPct !== 0 && predRangePct != null ? predRangePct / atrPct : null }
          : null;
      }
      setAtrMetrics(atrResultsFromPreds);
      
      // If backtest mode, prepare chart data with all trades
      if (isBacktest) {
        // allValidTrades is already in chronological order (oldest first)
        let tradesForTable = backtestDays ? allValidTrades.slice(-backtestDays) : allValidTrades;

        // --- Patch: Ensure last date in range is always included ---
        // Do not add a 'No prediction' row for the last date. Only show scored predictions.

        // Recalculate cumulative returns and fees for the limited trades to ensure accuracy
        let cumulativeReturn = 1;
        let tablePositionTracker = 0; // Track position within the table to recalculate fees correctly
        const tradesWithRecalculatedCumulative = tradesForTable.map(t => {
          // If this is a 'No prediction' row, just carry forward cumulativeReturn
          if (t.id === 'no-prediction') {
            return {
              ...t,
              cumulativeReturn: cumulativeReturn
            };
          }
          let recalculatedFee = t.feeCharged;
          if (t.direction !== 0) {
            recalculatedFee = (t.direction === tablePositionTracker) ? 0 : tradingFee;
            tablePositionTracker = t.direction;
          } else {
            tablePositionTracker = 0;
          }
          let recalculatedDailyReturn = t.dailyReturn;
          if (t.direction !== 0) {
            recalculatedDailyReturn = ((t.actualPrice - t.currentPrice) / t.currentPrice) * t.direction - recalculatedFee;
          }
          cumulativeReturn *= (1 + (recalculatedDailyReturn || 0));
          return {
            ...t,
            feeCharged: recalculatedFee,
            dailyReturn: recalculatedDailyReturn,
            pnl: t.direction !== 0 ? (recalculatedDailyReturn * 100) : 0,
            cumulativeReturn: cumulativeReturn
          };
        });

        setBacktestTrades(tradesWithRecalculatedCumulative);

        // Build chart data: cumulative P/L over time (use the recalculated trades)
        const filteredTrades = tradesWithRecalculatedCumulative
          .filter(t => t.date !== 'Unknown' && t.id !== 'no-prediction');
        const chartSeries = filteredTrades.map(t => ({
            x: new Date(t.date).getTime(),
            y: parseFloat(((t.cumulativeReturn - 1) * 100).toFixed(2))
          }));
        setBacktestChartData(chartSeries);
        // Build actual price series for drawprice mode
        const priceSeries = filteredTrades
          .filter(t => t.actualPrice != null && isFinite(t.actualPrice))
          .map(t => ({
            x: new Date(t.date).getTime(),
            y: parseFloat(t.actualPrice.toFixed(2))
          }));
        setBacktestPriceData(priceSeries);
      } else {
        setBacktestTrades([]);
        setBacktestChartData([]);
        setBacktestPriceData([]);
      }
    } catch (err) {
      console.error('Error calculating historical P/L:', err);
      setHistoricalPnL({ three_day: null, seven_day: null, thirty_day: null, ninety_day: null });
      setBacktestTrades([]);
      setBacktestChartData([]);
      setBacktestPriceData([]);
    }
  };

  // Apply backtest parameter changes and update URL
  const handleApplyBacktestParams = () => {
    setBacktestCashStrategyParam(pendingCashStrategyParam);
    setBacktestRoundFeeParam(pendingRoundFeeParam);
    try {
      const sp = new URLSearchParams(window.location.search || '');
      sp.set('cashstrategy', pendingCashStrategyParam);
      sp.set('roundfee', pendingRoundFeeParam);
      window.history.replaceState({}, '', `${window.location.pathname}?${sp.toString()}`);
    } catch (e) { /* ignore URL update errors */ }
  };

  // Member research, as opposed to a system forecast. Decides which of the two
  // panels below the prediction table is shown.
  const isMemberResearch = String(prediction?.model || '').startsWith('user:');

  // Who may change whether this is shared. The server decides for real -- it
  // re-reads is_admin from the users row -- so this only governs whether the
  // control is worth drawing.
  const viewer = getLoggedinUser() || {};
  const viewerId = viewer.id ?? viewer.user?.id ?? null;
  const isAdmin = !!(viewer.is_admin ?? viewer.user?.is_admin);
  const isOwnerOrAdmin = isAdmin
    || (viewerId != null && Number(prediction?.user_id) === Number(viewerId));
  const canShare = isMemberResearch && !isCleanMode && isOwnerOrAdmin;
  const isShared = !prediction?.is_private;

  // Publishing a question to Blogger, or refreshing the post it already has.
  // Administrators only -- unlike grading or sharing, this acts on an
  // external, publicly indexed page rather than on the member's own record,
  // so it is not something ownership alone should unlock.
  //
  // Offered whether or not the question has settled. A forecast is the part a
  // reader arrives for, and publishing only on settlement put the site's one
  // shareable page about a question up on the day the answer stopped being
  // interesting. Settling then rewrites that same post in place -- see
  // publishOrUpdate in bloggerSyncService -- so publishing early costs the
  // record nothing.
  //
  // Shared research only: a private forecast has nothing to publish, and the
  // server refuses it anyway.
  const canPublishBlogger = isMemberResearch && !isCleanMode && isAdmin && isShared;

  /*
   * Refiling an answer under a different model. Administrators only.
   *
   * The save path asks the member "is this DeepSeek?" only when the answer
   * collides with one already in the group -- a model that names itself wrong
   * on a question nothing else answered is stored wrong and nothing notices.
   * This is the correction for those, made where the answer can be read.
   *
   * The catalogue comes from the same options endpoint the research wizard
   * uses, so a model added on the server appears here without a release.
   */
  const canRefileModel = isMemberResearch && !isCleanMode && isAdmin;
  const [modelCatalogue, setModelCatalogue] = useState([]);
  useEffect(() => {
    if (!canRefileModel || modelCatalogue.length) return;
    let cancelled = false;
    rawApi.get(`${API}/api/user-predictions/options`, { headers: getAuthHeaders() })
      .then((res) => {
        const models = res?.data?.data?.models || res?.data?.models || [];
        if (!cancelled) setModelCatalogue(models);
      })
      // No catalogue means no refile control, which is the same as not being an
      // administrator. Nothing else on the page depends on it.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canRefileModel, modelCatalogue.length]);

  const refileModel = async (id, modelId) => {
    await rawApi.patch(
      `${API}/api/user-predictions/predictions/${id}/model`,
      { model: modelId },
      { headers: getAuthHeaders() }
    );
    // Every panel on the page names the model somewhere; re-reading the record
    // is cheaper than threading the new name through each of them.
    setReloadKey((k) => k + 1);
  };
  const bloggerPostUrl = prediction?.blogger_post?.url || null;

  /*
   * Settling, from the page the forecast is actually read on.
   *
   * The grade button lived only in the research list. But the list is a row of
   * numbers, and the page somebody opens to decide what the outcome WAS is this
   * one -- it carries the ranges, the models' own prices, and the chart script
   * that reads back the four figures the form asks for. Sending them back to
   * the list to type those in was a round trip for no reason.
   *
   * Same gate as sharing: owner or admin. This page is public for shared
   * research, so a reader who followed a link must not be offered it -- and the
   * server checks again regardless.
   *
   * The detail endpoint renames the columns on the way out (range_min becomes
   * prediction_low, input_time becomes create_date), so the row has to be
   * mapped back to what the dialog and the list both speak. The group is
   * already in the list's own spelling and passes through untouched.
   */
  const windowClosed = prediction?.target_date
    ? new Date(prediction.target_date).getTime() <= Date.now()
    : false;
  const canSettle = isMemberResearch && !isCleanMode && isOwnerOrAdmin
    && !prediction?.is_scored && windowClosed;

  const settleTarget = canSettle ? {
    id: prediction.id,
    symbol: prediction.symbol,
    timeframe: prediction.timeframe,
    input_time: prediction.create_date,
    valid_until: prediction.target_date,
    current_price: prediction.current_price,
    range_min: prediction.prediction_low,
    range_max: prediction.prediction_high,
    model_label: prediction.model_label,
    model: prediction.model,
    model_reported_price: prediction.model_reported_price,
  } : null;

  // Every other unsettled answer to the same question. Settling covers the
  // group, so the dialog shows the group -- and prefills the baseline from what
  // those models reported, which needs all of them.
  const settleSiblings = (prediction?.prompt_group || []).filter((r) => !r.is_scored);

  const toggleShare = async () => {
    setSharing(true);
    setShareError(null);
    try {
      // The whole group. Publishing three answers out of five leaves a
      // comparison table with rows missing and nothing on the page to say so.
      await setResearchVisibility(prediction.id, !isShared, 'group');
      // Reflect it locally rather than refetching: the only thing that changed
      // is a flag this page already holds, and a refetch would redraw the chart.
      setPrediction((prev) => (prev ? { ...prev, is_private: isShared ? 1 : 0 } : prev));
    } catch (e) {
      setShareError(
        (typeof e === 'string' && e) || e?.message || 'Could not change sharing. Try again.'
      );
    } finally {
      setSharing(false);
    }
  };

  const handleUpdateBlogger = async () => {
    setUpdatingBlogger(true);
    setBloggerUpdateMsg(null);
    try {
      const res = await updatePredictionBlogger(prediction.id);
      // The URL practically never changes -- Blogger keeps the permalink it
      // assigned at creation even when the title changes -- but the server is
      // the source of truth for it, so a moved URL is reflected rather than
      // silently pointing the link at a stale one.
      const url = res?.data?.url || res?.url || prediction?.blogger_post?.url;
      if (url) setPrediction((prev) => (prev ? { ...prev, blogger_post: { url } } : prev));
      setBloggerUpdateMsg({ type: 'success', text: 'Blogger post updated.' });
    } catch (e) {
      setBloggerUpdateMsg({
        type: 'danger',
        text: (typeof e === 'string' && e) || e?.message || 'Could not update the Blogger post.',
      });
    } finally {
      setUpdatingBlogger(false);
    }
  };

  // What to call the model on screen. prediction.model is the storage key and
  // carries a 'user:' prefix that separates member research from system
  // forecasts in queries; the label is what a reader should see.
  const modelLabel = prediction?.model_label
    || String(prediction?.model || '').replace(/^user:/, '')
    || '';

  // What the heading and the browser tab call it.
  //
  // "by Grok" and "by Grok 4.5" are not the same claim, and the version is the
  // thing a reader coming back to a forecast needs -- the whole point of asking
  // the same family twice months apart is that the versions differ. The title is
  // also what a shared link shows and what a search engine indexes, so it is the
  // copy most worth being exact in.
  //
  // Which is also why the claim may only refine the name, never replace it. See
  // displayModelName: a model that names a different family is confabulating,
  // and this heading must not repeat the confabulation as a byline.
  //
  // modelLabel stays as it was: it is what the self-report is checked against,
  // and comparing a claim to itself would check nothing.
  const displayModel = displayModelName(
    modelLabel,
    String(prediction?.model || '').replace(/^user:/, ''),
    prediction?.model_self_report,
  );

  // The browser tab, and the title a search engine indexes. For a shared link
  // it is the first thing a reader sees, and it was the app's default title for
  // every forecast alike.
  //
  // The instant the forecast was made is part of it, to the minute, because
  // symbol + horizon + model is not unique: the same model is asked about the
  // same symbol over the same horizon again and again. Across the table that
  // triple repeats on 99.8% of rows. Adding the date alone is not enough
  // either -- daily-horizon forecasts are re-run within the day and hourly ones
  // up to 27 times, leaving 5% of the last 90 days still sharing a title. The
  // minute brings that to 0.2%.
  //
  // UTC, like every other timestamp on the page, so the title and the body
  // cannot appear to disagree for a reader outside UTC.
  // Rebuilt only when the forecast changes. It is a few hundred characters of
  // string building, but it runs on every keystroke of unrelated state without
  // this, and returns null for a forecast with no range or no window rather
  // than offering a script that would draw nothing.
  const pineScript = React.useMemo(
    () => (prediction ? buildPineScript(prediction) : null),
    [prediction]
  );

  // Null for a forecast with no siblings -- fifteen of them predate prompt
  // storage and have nothing to compare against, and a button offering to chart
  // one model against itself is worse than no button.
  const comparePineScript = React.useMemo(
    () => (prediction ? buildComparePineScript(prediction) : null),
    [prediction]
  );

  useEffect(() => {
    if (!prediction?.symbol) return;
    const horizon = formatHorizon(prediction.timeframe, prediction.prediction_window_days);
    const madeAt = prediction.create_date ? new Date(prediction.create_date) : null;
    const stamp = madeAt && !Number.isNaN(madeAt.getTime())
      ? `${madeAt.toISOString().replace('T', ' ').slice(0, 16)} UTC`
      : null;
    document.title = [
      prediction.symbol,
      horizon,
      isReasonMode ? 'forecast reasoning' : 'forecast',
      displayModel ? `by ${displayModel}` : null,
      stamp ? `· ${stamp}` : null,
    ].filter(Boolean).join(' ') + ' | ForecastNest';
  }, [prediction, isReasonMode, displayModel]);

  // Fetch historical P/L (also computes ATR metrics) when prediction loads
  useEffect(() => {
    if (prediction && prediction.model && prediction.symbol && prediction.create_date) {
      calculateHistoricalPnL(prediction);
    }
  }, [prediction, isBacktest, backtestDays, backtestCashStrategyParam, backtestRoundFeeParam]);
  useEffect(() => {
    const t = setTimeout(() => setShowChart(true), 200);
    return () => clearTimeout(t);
  }, []);

  // --- TradingView symbol/interval logic to match Report page ---
  const getTvSymbol = (symbol) => {
    if (!symbol) return 'BTC';
    if (symbol === 'SPY') return 'AMEX:SPY';
    if (symbol === 'TQQQ') return 'NASDAQ:TQQQ';
    if (symbol === 'GUSH') return 'AMEX:GUSH';
    if (symbol === 'TMF') return 'AMEX:TMF';
    if (symbol === 'TNA') return 'AMEX:TNA';
    if (symbol === 'UGL') return 'AMEX:UGL';
    if (symbol === 'SOXL') return 'AMEX:SOXL';
    if (symbol === 'FAS') return 'AMEX:FAS';
    if (symbol === 'UCO') return 'AMEX:UCO';
    if (symbol === 'IBIT') return 'NASDAQ:IBIT';
    const map = {
      BTC: 'COINBASE:BTCUSD',
      ETH: 'COINBASE:ETHUSD',
      XRP: 'COINBASE:XRPUSD',
      SOL: 'COINBASE:SOLUSD',
      AVAX: 'COINBASE:AVAXUSD',
      ADA: 'COINBASE:ADAUSD',
      ARB: 'COINBASE:ARBUSD',
      LINK: 'COINBASE:LINKUSD',
      UNI: 'COINBASE:UNIUSD',
      DOGE: 'COINBASE:DOGEUSD',
    };
    return map[symbol] || symbol;
  };
  const tvSymbol = getTvSymbol(prediction?.symbol);

  // Match the widget's granularity to the forecast horizon. A 4-hour forecast
  // drawn on daily candles is one bar wide and says nothing.
  const tvInterval = (() => {
    const tf = String(prediction?.timeframe || '').toLowerCase();
    if (tf === '1h') return '15';
    if (tf === '4h') return '60';
    if (tf === '1d' || tf === '2d') return '60';
    if (tf === '7d') return 'D';
    return 'D';
  })();

  // Whether the API returned a candle series to draw.
  //
  // It does not for member research: serving OHLC to a browser is display use
  // of licensed market data, and there is no commercial licence behind this
  // project. Those forecasts get the TradingView widget instead — TradingView's
  // own data under TradingView's own licence, embedded rather than
  // redistributed, on a page that is free to view, which is the arrangement
  // their terms describe.
  //
  // System forecasts still carry candles and still draw the annotated chart:
  // the forecast band and the actual path together are the whole point of it.
  const hasCandles = Array.isArray(prediction?.candles) && prediction.candles.length > 1;

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === '') return '—';
    const num = parseFloat(price);
    if (isNaN(num)) return '—';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
  };

  const formatPercentageChange = (value, basePrice) => {
    if (value === null || value === undefined || value === '' || basePrice === null || basePrice === undefined || basePrice === '' || basePrice === 0) return '—';
    const numValue = parseFloat(value);
    const numBase = parseFloat(basePrice);
    if (isNaN(numValue) || isNaN(numBase) || numBase === 0) return '—';
    const percentage = ((numValue - numBase) / numBase) * 100;
    const sign = percentage >= 0 ? '+' : '';
    return `${sign}${percentage.toFixed(2)}%`;
  };

  const formatConfidence = (conf) => {
    if (conf === null || conf === undefined || conf === '') return '—';
    const num = parseFloat(conf);
    if (isNaN(num)) return '—';
    return `${num.toFixed(1)}%`;
  };

  const formatPerformance = (perf) => {
    if (perf === null || perf === undefined || perf === '' || perf === 0) return '—';
    const num = parseFloat(perf);
    if (isNaN(num)) return '—';
    return num.toFixed(2);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return dateStr.toString().slice(0, 16).replace('T', ' ');
  };

  // Returns the current time in UTC in the same format as formatDate (YYYY-MM-DD HH:mm)
  const formatUtcNow = () => {
    const now = new Date();
    return now.toISOString().slice(0, 16).replace('T', ' ');
  };

  const calculateTimeframe = (createDate, targetDate) => {
    if (!createDate || !targetDate) return '—';
    
    const create = new Date(createDate);
    const target = new Date(targetDate);
    
    if (isNaN(create.getTime()) || isNaN(target.getTime())) return '—';
    
    const diffMs = target.getTime() - create.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    
    // Round to nearest standard timeframe
    if (diffHours <= 6) return '4 hours';
    else if (diffDays <= 1.5) return '1 day';
    else if (diffDays <= 3) return '2 days';
    else if (diffDays <= 14) return '7 days';
    else if (diffDays <= 60) return '30 days';
    else return '180 days';
  };

  // mt-5 on both early returns clears the fixed navbar the public routes get.
  // Harmless inside the app layout, which offsets its own header.
  if (loading) {
    return (
      <Container fluid className="py-4 mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading prediction details...</p>
        </div>
      </Container>
    );
  }

  if (errorMsg || !prediction) {
    const signedIn = !!(getLoggedinUser() || {}).token;

    // Same stash the public top bar uses, so signing in from either place
    // returns to the forecast that was asked for.
    const rememberWhereWeWere = () => {
      try {
        sessionStorage.setItem(
          "postLoginRedirect",
          `${window.location.pathname}${window.location.search || ""}`
        );
      } catch { /* storage refused; sign-in still works */ }
    };

    const SignInLink = ({ children }) => (
      <Link to="/login" className="btn btn-primary btn-sm" onClick={rememberWhereWeWere}>
        {children}
      </Link>
    );

    /*
     * What went wrong, in words meant for whoever is reading.
     *
     * These pages get shared, so most people arriving at a failure followed a
     * link from somewhere else and have no idea what this site is. "Could not
     * load this forecast" told them nothing they could act on, and the API's
     * own wording -- "Access denied to private prediction" -- is written for a
     * developer reading a response body.
     *
     * The three real cases need three different answers: a link to something
     * that never existed, a forecast somebody chose not to share, and a paid
     * one. Only the second is fixed by signing in, and offering sign-in for
     * the others would send people down a road that does not lead anywhere.
     */
    let heading = 'We could not load this forecast';
    let detail = errorMsg
      || 'Something went wrong on our side. Refreshing the page usually fixes it.';
    let action = null;

    if (errorCode === 404) {
      heading = 'This forecast does not exist';
      detail = 'The link may be mistyped, or it may point to a forecast that has since been removed.';
      action = <Link to="/" className="btn btn-primary btn-sm">Go to ForecastNest</Link>;
    } else if (errorKind === 'private_forecast' || (errorCode === 403 && !errorKind)) {
      heading = 'This forecast is private';
      detail = signedIn
        ? 'The member who recorded it has not shared it. Only they and an administrator can open it.'
        : 'The member who recorded it chose not to publish it. If it was shared with you, sign in with the account it was shared with.';
      action = signedIn
        ? <Link to="/my-research" className="btn btn-primary btn-sm">Browse shared research</Link>
        : <SignInLink>Sign in</SignInLink>;
    } else if (errorKind === 'internal_only') {
      heading = 'This one is not published';
      // Deliberately no upgrade prompt. Nothing anyone can buy opens this, and
      // offering a membership that would not help is worse than a plain no.
      detail = errorMsg
        || 'This is one of our internal forecasts. Members\u2019 own research, and what they have chosen to share, is on the research pages.';
      action = <Link to="/research" className="btn btn-primary btn-sm">Browse shared research</Link>;
    }

    return (
      <Container fluid className="py-5 mt-5">
        <Row className="justify-content-center">
          <Col md={7} lg={5}>
            <div className="text-center">
              <h4 className="mb-2">{heading}</h4>
              <p className="text-muted">{detail}</p>
              {action}
              {!signedIn && errorCode === 404 && (
                <p className="text-muted fs-13 mt-3 mb-0">
                  Some forecasts are only visible to the member who recorded them.
                </p>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

    // Note: Do NOT early-return when clean mode is enabled here — Layout handles hiding
    // header/sidebar/footer for clean/embed mode. The PredictionDetail page should still
    // render the full prediction content so users can share/embed the same full content
    // without the surrounding chrome.

  return (
    <>
      {/* Custom styles */}
      <style>
        {`
          .prediction-detail-page {
            min-height: 100vh;
            background-color: #f8f9fa;
            position: relative;
            z-index: 100;
          }
          
          .detail-content {
            width: 80%;
            max-width: 1200px;
            margin: 0 auto;
          }
          /* Clean-mode bottom branding bar */
          .forecastnest-cleanbar {
            position: fixed;
            left: 0;
            right: 0;
            bottom: 0;
            height: 5vh; /* ~5% of viewport height */
            background: rgba(0,0,0,0.55);
            color: #ffffff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.95rem;
            z-index: 2147483647; /* be above everything in clean/embed */
            pointer-events: none; /* should not block interactions */
            -webkit-font-smoothing: antialiased;
          }
          .forecastnest-watermark {
            position: fixed;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%) rotate(-30deg);
            width: 200vw; /* extend beyond viewport so pattern repeats */
            text-align: center;
            /* Use a slightly darker, still subtle color so watermark is visible on page's light background */
            color: rgba(0,0,0,0.12); /* visible but not distracting */
            font-weight: 700;
              font-size: 1.2vw; /* slightly larger so watermark is legible but subtle */
            letter-spacing: 0.12em;
            pointer-events: none;
            z-index: 1; /* placed behind the page content (which uses higher z-index) */
            white-space: nowrap;
            user-select: none;
            mix-blend-mode: normal; /* avoid hiding on light backgrounds */
              filter: blur(0.3px); /* a touch of softness but clearer */
              opacity: 0.2; /* more visible while still subtle */
          }

          /* Mobile responsive content width */
          @media (max-width: 992px) {
            .detail-content { 
              width: 100% !important; 
              max-width: none !important; 
              padding-left: 8px; 
              padding-right: 8px; 
            }
          }
          
          .detail-section {
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            margin-bottom: 1.5rem;
          }
          
          .expand-hint {
            position: absolute;
            bottom: 8px;
            right: 12px;
            font-size: 0.85rem;
            color: #666;
            background: rgba(255,255,255,0.92);
            padding: 2px 8px;
            border-radius: 12px;
          }

          /* Price badge colors - lighter theme matching AI model family colors */
          .price-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 32px;
            width: 100px;
            padding: 6px 12px;
            border-radius: 6px;
            font-weight: 500;
            font-size: 0.875rem;
            border: 1px solid;
            text-align: center;
          }
          
          .price-badge-low {
            background-color: #e1f5fe !important;
            border-color: #b3e5fc !important;
            color: #0277bd !important;
          }
          
          .price-badge-current {
            background-color: #e8f5e8 !important;
            border-color: #c8e6c9 !important;
            color: #2e7d32 !important;
          }
          
          .price-badge-high {
            background-color: #f4faf4 !important;
            border-color: #eef7ee !important;
            color: #388e3c !important;
          }
          
          .price-badge-actual {
            background-color: #fdf1db !important;
            border-color: #f4e1a6 !important;
            color: #f57c00 !important;
          }

          /* Info labels consistent width for table-like layout */
          .info-label {
            display: inline-block;
            width: 100px;
            text-align: center;
            margin-bottom: 0;
          }
          
          .info-item {
            display: flex;
            flex-direction: column;
            width: 110px;
            min-width: 110px;
            align-items: center;
            text-align: center;
          }

          .price-item {
            display: flex;
            flex-direction: column;
            width: 110px;
            min-width: 110px;
            align-items: center;
            text-align: center;
          }

          /* Mobile responsive styles */
          @media (max-width: 768px) {
            .unified-card {
              flex-direction: column !important;
            }
            
            /* Stacked, so the side-by-side swap stops meaning anything: reset
               the order and let DOM order decide, which puts the short summary
               above the long prompt. Without this, the order property still
               applies when
               the flex direction is column and the two would stack in the
               swapped order, with the divider on the wrong one. */
            .unified-card > div {
              order: 0 !important;
            }

            /* The vertical divider becomes a horizontal one. It is carried by
               the right-hand column on desktop, which is always this one once
               the order above is reset. */
            .reason-section-mobile {
              border-left: none !important;
              border-top: 1px solid #eee;
              margin-left: 0 !important;
              margin-top: 24px;
              padding-left: 0 !important;
              padding-top: 24px;
            }

            /* Member research puts the divider on the info column instead; on a
               narrow screen it is the one on top, so it carries none. */
            .unified-card > div:first-child {
              border-left: none !important;
              margin-left: 0 !important;
              padding-left: 0 !important;
            }

            .prediction-table {
              flex-direction: column;
            }

            .price-section-mobile {
              margin-bottom: 0.5rem;
            }

            .info-section-mobile {
              margin-top: 0.5rem;
              margin-left: 0 !important;
            }

            .table-row-mobile {
              margin-bottom: 0.75rem;
            }
          }
        `}
      </style>

      <div className="prediction-detail-page">
        <Container fluid className="py-4">
          <div className="detail-content">
            {/* Centered Title */}
            {/* Clearing the fixed navbar the public routes render, since the
                layout adds no offset of its own.

                The bar is about 72px: 10px of padding either side of nav links
                that carry 14px of their own plus their line height. Guessing it
                at 58 is what produced two wrong answers in a row — 64px total
                hid the heading behind the bar completely, and 96px left it
                pressed against the underside. mt-5 pt-5 with the container's
                py-4 is 120px, which leaves a real gap.

                The whitespace worth trimming was inside this block, not above
                it: a heading, three timestamp rows and a three-line disclaimer
                stacked with generous gaps is what pushed the first card down.
                mb-4 at the end so the disclaimer stops reading as part of that
                card. */}
            <div className="text-center mb-4 mt-5 pt-5">
              <h2 className="mb-2 text-primary">
                {/* The horizon is what separates two forecasts on the same
                    symbol by the same model, so it belongs in the heading. */}
                {[prediction.symbol,
                  formatHorizon(prediction.timeframe, prediction.prediction_window_days),
                ].filter(Boolean).join(' ')} Prediction{isReasonMode ? ' Reason' : ''} by {displayModel}
                {displayModel && (displayModel.toLowerCase().includes('gpt') || displayModel.toLowerCase().includes('claude') || displayModel.toLowerCase().includes('gemini')) ? ' (AI)' : ''}
              </h2>
              {/* Subtitle with labeled rows: created, target, current time */}
              <div className="text-muted mb-0 fs-6 prediction-subtitle" style={{ lineHeight: 1.3, maxWidth: '320px', margin: '0 auto', textAlign: 'left' }}>
                <div className="d-flex justify-content-between" style={{ gap: 12 }}>
                  <div style={{ fontWeight: 600 }}>Prediction Created at:</div>
                  <div style={{ fontFamily: 'monospace' }}>{formatDate(prediction.create_date)} UTC</div>
                </div>
                <div className="d-flex justify-content-between" style={{ gap: 12 }}>
                  <div style={{ fontWeight: 600 }}>Prediction Target at:</div>
                  <div style={{ fontFamily: 'monospace' }}>{formatDate(prediction.target_date)} UTC</div>
                </div>
                <div className="d-flex justify-content-between" style={{ gap: 12 }}>
                  <div style={{ fontWeight: 600 }}>Current Time:</div>
                  <div style={{ fontFamily: 'monospace' }}>{formatUtcNow()} UTC</div>
                </div>
              </div>
              {/* Directly under the title, on every page that shows a forecast.
                  A forecast is the thing most easily mistaken for advice, and
                  these pages are public and shared -- a reader arriving from a
                  link has seen nothing else of the site. Below the fold it
                  would be read by the people who least need it. */}
              <div className="mx-auto mt-3" style={{ maxWidth: 720 }}>
                <ResearchDisclaimer />
              </div>
            </div>
            

            {/* Unified Info/Reason Card (hidden when chartOnly is true) */}
            {!isChartOnly && (
            <Row className="mb-4 g-0">
              <Col>
                <div className="detail-section p-4 d-flex flex-row unified-card" style={{ minHeight: 180 }}>
                  {/*
                      Info column. On member research it swaps sides with the
                      prompt: the comparison table is narrow and fixed-height
                      while the prompt and reasoning are long, so putting the
                      long content in the first column lets the short one sit
                      beside it instead of leaving a column of white space.
                      Done with flex order rather than by moving the markup, so
                      the reading order in the DOM stays the same for anything
                      that does not lay out in columns -- narrow screens, print,
                      a screen reader.
                  */}
                  <div style={{
                    flex: 1,
                    minWidth: 0,
                    order: isMemberResearch ? 2 : 1,
                    ...(isMemberResearch
                      ? { borderLeft: '1px solid #eee', marginLeft: 32, paddingLeft: 32 }
                      : {}),
                  }}>
                    {/* On member research the heading labelled a table that is no
                        longer there — the comparison below carries its own. */}
                    {!isMemberResearch && (
                      <h5 className="text-dark mb-3">Prediction Information</h5>
                    )}
                    
                          {/* Desktop: Side by side layout, Mobile: Stacked layout */}
                          {isCleanMode && (
                            <>
                              <div className="forecastnest-cleanbar">forecastnest.com | AI Crypto Forecasting Platform</div>
                              <div className="forecastnest-watermark">forecastnest.com | AI Crypto Forecasting Platform</div>
                            </>
                          )}
                    <div className="prediction-table d-flex flex-column flex-md-column flex-lg-column">
                      {/*
                          Hidden on member research.
                      
                          Every figure here is either restated better below or belongs to a
                          frame this page is moving away from. Confidence and score appear per
                          model in the comparison table, where they can be compared; price when
                          asked, the close, the move and the actual range are shared by the
                          whole group and are stated once above it. What is left is
                          "Suggestion: HOLD" -- a trading instruction, which is not what a
                          member recording their own AI research asked for, and not something
                          this product should appear to be giving.
                      */}
                      {!isMemberResearch && (<>
                      {/* Header row: Confidence & Score */}
                      <div className="d-flex align-items-center mb-2 table-row-mobile" style={{ justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{ width: '110px', textAlign: 'left' }}>
                            <span className="fw-bold" style={{ color: '#2e7d32' }}>Confidence</span>
                          </div>
                          <div style={{ width: '140px', textAlign: 'left' }}>
                            <span className="fw-bold" style={{ color: '#2e7d32' }}>{formatConfidence(prediction.confidence)}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{ width: '110px', textAlign: 'left' }}>
                            <span className="fw-bold" style={{ color: '#388e3c' }}>Score</span>
                          </div>
                          <div style={{ width: '140px', textAlign: 'left' }}>
                            <span className="fw-bold" style={{ color: '#388e3c' }}>{prediction.actual_price ? (prediction.performance != null ? formatPerformance(prediction.performance) : '-') : '-'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Suggestion row */}
                      <div className="d-flex align-items-center mb-3 table-row-mobile" style={{ justifyContent: 'space-between', paddingBottom: '12px', borderBottom: '1px solid #e0e0e0' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{ width: '110px', textAlign: 'left' }}>
                            <span className="fw-bold">Suggestion</span>
                          </div>
                          <div style={{ width: '140px', textAlign: 'left' }}>
                            <span className="fw-bold" style={{ color: calculateSuggestion(prediction, backtestCashStrategyParam).color }}>{calculateSuggestion(prediction, backtestCashStrategyParam).label}</span>
                          </div>
                        </div>
                      </div>

                      {/* Main rows: Low / Actual Low ; Price@Pred / Actual ; High / Actual High */}
                      <div className="d-flex align-items-start table-row-mobile" style={{ gap: 32 }}>
                        {/* Left column: prediction values */}
                        <div style={{ flex: 1 }}>
                          {/* Low */}
                          <div className="d-flex align-items-center mb-2 table-row-mobile">
                            <div style={{ width: '110px', textAlign: 'left' }}>
                              <span className="fw-bold" style={{ color: '#0277bd' }}>Low</span>
                            </div>
                            <div style={{ textAlign: 'left' }}>
                              <span className="fw-bold" style={{ color: '#0277bd' }}>{formatPercentageChange(prediction.prediction_low, prediction.current_price ?? prediction.currentPrice ?? prediction.price_at_prediction ?? prediction.priceAtPrediction ?? prediction.price)}</span>
                            </div>
                          </div>

                          {/* Price@Pred */}
                          <div className="d-flex align-items-center mb-2 table-row-mobile">
                            <div style={{ width: '110px', textAlign: 'left' }}>
                              <span className="fw-bold" style={{ color: '#2e7d32' }}>Price@Pred</span>
                            </div>
                            <div style={{ textAlign: 'left' }}>
                              <span className="fw-bold" style={{ color: '#2e7d32' }}>{formatPrice(prediction.current_price ?? prediction.currentPrice ?? prediction.price_at_prediction ?? prediction.priceAtPrediction ?? prediction.price)}</span>
                            </div>
                          </div>

                          {/* High */}
                          <div className="d-flex align-items-center mb-2 table-row-mobile">
                            <div style={{ width: '110px', textAlign: 'left' }}>
                              <span className="fw-bold" style={{ color: '#388e3c' }}>High</span>
                            </div>
                            <div style={{ textAlign: 'left' }}>
                              <span className="fw-bold" style={{ color: '#388e3c' }}>{formatPercentageChange(prediction.prediction_high, prediction.current_price ?? prediction.currentPrice ?? prediction.price_at_prediction ?? prediction.priceAtPrediction ?? prediction.price)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Right column: actuals */}
                        <div style={{ width: 260 }}>
                          {/* Actual Low */}
                          <div className="d-flex align-items-center mb-2 table-row-mobile">
                            <div style={{ width: '110px', textAlign: 'left' }}>
                              <span className="fw-bold" style={{ color: '#f57c00' }}>Actual Low</span>
                            </div>
                            <div style={{ textAlign: 'left' }}>
                              <span className="fw-bold" style={{ color: '#f57c00' }}>{(prediction.actual_low != null && prediction.actual_low !== '') ? formatPercentageChange(prediction.actual_low, prediction.current_price ?? prediction.currentPrice ?? prediction.price_at_prediction ?? prediction.priceAtPrediction ?? prediction.price) : '-'}</span>
                            </div>
                          </div>

                          {/* Actual */}
                          <div className="d-flex align-items-center mb-2 table-row-mobile">
                            <div style={{ width: '110px', textAlign: 'left' }}>
                              <span className="fw-bold" style={{ color: '#f57c00' }}>Actual</span>
                            </div>
                            <div style={{ textAlign: 'left' }}>
                              <span className="fw-bold" style={{ color: '#f57c00' }}>{prediction.actual_price ? formatPercentageChange(prediction.actual_price, prediction.current_price ?? prediction.currentPrice ?? prediction.price_at_prediction ?? prediction.priceAtPrediction ?? prediction.price) : '-'}</span>
                            </div>
                          </div>

                          {/* Actual High */}
                          <div className="d-flex align-items-center mb-2 table-row-mobile">
                            <div style={{ width: '110px', textAlign: 'left' }}>
                              <span className="fw-bold" style={{ color: '#f57c00' }}>Actual High</span>
                            </div>
                            <div style={{ textAlign: 'left' }}>
                              <span className="fw-bold" style={{ color: '#f57c00' }}>{(prediction.actual_high != null && prediction.actual_high !== '') ? formatPercentageChange(prediction.actual_high, prediction.current_price ?? prediction.currentPrice ?? prediction.price_at_prediction ?? prediction.priceAtPrediction ?? prediction.price) : '-'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      </>)}

                      {/* Historical P/L Section - only show when NOT in clean mode */}
                      {/*
                          Member research gets the model comparison instead of this panel.
                          What follows measures a trading rule derived from the forecast —
                          rolling return, win rate, drawdown — which is the wrong frame on a
                          research notebook, and answers a question nobody asked: a member
                          asks several models one question and wants to know how the answers
                          differed. System forecasts keep it; they are generated per model
                          with no shared prompt, so there is no group to compare.
                      */}
                      {!isMemberResearch && !isCleanMode && (() => {
                        const periodLabel = prediction?.timeframe === '1h' ? 'Hr' : prediction?.timeframe === '4h' ? '4-Hr' : prediction?.timeframe === '7d' ? 'Wk' : prediction?.timeframe === '30d' ? 'Mo' : 'Day';
                        return (
                      <div style={{ marginTop: 24, borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
                        <h6 className="text-dark mb-3">Model Performance (Same Asset)</h6>
                        <div className="d-flex flex-wrap gap-3">
                          {/* 3-Period P/L */}
                          <div style={{ flex: 1, minWidth: 120 }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>3-{periodLabel} P/L</div>
                            <a href={(() => { const sp = new URLSearchParams(window.location.search || ''); sp.set('backtest', 'true'); sp.set('days', '3'); return `?${sp.toString()}`; })()} style={{ textDecoration: 'none' }}>
                              <div style={{ fontSize: '16px', fontWeight: 'bold', color: historicalPnL.three_day !== null && historicalPnL.three_day >= 0 ? '#2e7d32' : '#c62828', cursor: 'pointer', transition: 'opacity 0.2s' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>
                                {historicalPnL.three_day !== null ? `${historicalPnL.three_day >= 0 ? '+' : ''}${(historicalPnL.three_day * 100).toFixed(2)}%` : '-'}
                              </div>
                            </a>
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.4 }}>
                              <div>ATR: {atrMetrics.three_day ? `${atrMetrics.three_day.atr.toFixed(2)}%` : '-'}</div>
                              <div>Range: {atrMetrics.three_day && atrMetrics.three_day.predRange != null ? `${atrMetrics.three_day.predRange.toFixed(2)}%` : '-'}</div>
                              <div>Range/ATR: {atrMetrics.three_day && atrMetrics.three_day.ratio != null ? atrMetrics.three_day.ratio.toFixed(2) : '-'}</div>
                            </div>
                          </div>

                          {/* 7-Period P/L */}
                          <div style={{ flex: 1, minWidth: 120 }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>7-{periodLabel} P/L</div>
                            <a href={(() => { const sp = new URLSearchParams(window.location.search || ''); sp.set('backtest', 'true'); sp.set('days', '7'); return `?${sp.toString()}`; })()} style={{ textDecoration: 'none' }}>
                              <div style={{ fontSize: '16px', fontWeight: 'bold', color: historicalPnL.seven_day !== null && historicalPnL.seven_day >= 0 ? '#2e7d32' : '#c62828', cursor: 'pointer', transition: 'opacity 0.2s' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>
                                {historicalPnL.seven_day !== null ? `${historicalPnL.seven_day >= 0 ? '+' : ''}${(historicalPnL.seven_day * 100).toFixed(2)}%` : '-'}
                              </div>
                            </a>
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.4 }}>
                              <div>ATR: {atrMetrics.seven_day ? `${atrMetrics.seven_day.atr.toFixed(2)}%` : '-'}</div>
                              <div>Range: {atrMetrics.seven_day && atrMetrics.seven_day.predRange != null ? `${atrMetrics.seven_day.predRange.toFixed(2)}%` : '-'}</div>
                              <div>Range/ATR: {atrMetrics.seven_day && atrMetrics.seven_day.ratio != null ? atrMetrics.seven_day.ratio.toFixed(2) : '-'}</div>
                            </div>
                          </div>

                          {/* 30-Period P/L */}
                          <div style={{ flex: 1, minWidth: 120 }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>30-{periodLabel} P/L</div>
                            <a href={(() => { const sp = new URLSearchParams(window.location.search || ''); sp.set('backtest', 'true'); sp.set('days', '30'); return `?${sp.toString()}`; })()} style={{ textDecoration: 'none' }}>
                              <div style={{ fontSize: '16px', fontWeight: 'bold', color: historicalPnL.thirty_day !== null && historicalPnL.thirty_day >= 0 ? '#2e7d32' : '#c62828', cursor: 'pointer', transition: 'opacity 0.2s' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>
                                {historicalPnL.thirty_day !== null ? `${historicalPnL.thirty_day >= 0 ? '+' : ''}${(historicalPnL.thirty_day * 100).toFixed(2)}%` : '-'}
                              </div>
                            </a>
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.4 }}>
                              <div>ATR: {atrMetrics.thirty_day ? `${atrMetrics.thirty_day.atr.toFixed(2)}%` : '-'}</div>
                              <div>Range: {atrMetrics.thirty_day && atrMetrics.thirty_day.predRange != null ? `${atrMetrics.thirty_day.predRange.toFixed(2)}%` : '-'}</div>
                              <div>Range/ATR: {atrMetrics.thirty_day && atrMetrics.thirty_day.ratio != null ? atrMetrics.thirty_day.ratio.toFixed(2) : '-'}</div>
                            </div>
                          </div>

                          {/* 90-Period P/L */}
                          <div style={{ flex: 1, minWidth: 120 }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>90-{periodLabel} P/L</div>
                            <a href={(() => { const sp = new URLSearchParams(window.location.search || ''); sp.set('backtest', 'true'); sp.set('days', '90'); return `?${sp.toString()}`; })()} style={{ textDecoration: 'none' }}>
                              <div style={{ fontSize: '16px', fontWeight: 'bold', color: historicalPnL.ninety_day !== null && historicalPnL.ninety_day >= 0 ? '#2e7d32' : '#c62828', cursor: 'pointer', transition: 'opacity 0.2s' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>
                                {historicalPnL.ninety_day !== null ? `${historicalPnL.ninety_day >= 0 ? '+' : ''}${(historicalPnL.ninety_day * 100).toFixed(2)}%` : '-'}
                              </div>
                            </a>
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.4 }}>
                              <div>ATR: {atrMetrics.ninety_day ? `${atrMetrics.ninety_day.atr.toFixed(2)}%` : '-'}</div>
                              <div>Range: {atrMetrics.ninety_day && atrMetrics.ninety_day.predRange != null ? `${atrMetrics.ninety_day.predRange.toFixed(2)}%` : '-'}</div>
                              <div>Range/ATR: {atrMetrics.ninety_day && atrMetrics.ninety_day.ratio != null ? atrMetrics.ninety_day.ratio.toFixed(2) : '-'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                        );
                      })()}

                      {/* Just the answers. The heading and the facts the group
                          shares sit with the prompt in the other column, since
                          those describe the question and the prompt is the
                          question. */}
                      {/* Rendered in clean mode too. Clean mode is for
                          screenshots, and on member research this table is the
                          content -- the P/L panel it replaced was excluded
                          there because it was noise, which this is not.
                          Without it a member forecast screenshots as an empty
                          panel, since the figures above are hidden for member
                          research as well. */}
                      {isMemberResearch && (
                        <>
                          <PromptGroupTable
                            group={prediction.prompt_group}
                            currentId={prediction.id}
                            baseline={prediction.current_price}
                            actualPrice={prediction.actual_price}
                            actualLow={prediction.actual_low}
                            actualHigh={prediction.actual_high}
                            // Every answer in the group is dated from the moment
                            // the prompt was built. Each model's own claimed
                            // timestamp is read against it.
                            askedAt={prediction.create_date}
                            refile={canRefileModel ? refileModel : null}
                            refileModels={canRefileModel ? modelCatalogue : []}
                          />
                          {/* Attribution under the answers: whose record this
                              is matters to somebody who followed a shared link,
                              but only once they have seen what is in it. */}
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
                            <span className="text-muted fs-12">
                              {prediction.author_name ? `submitted by: ${prediction.author_name}` : ''}
                            </span>
                            {/* Admin: reopen this question in the research
                                wizard to paste in a model that was missed. */}
                            {canRefileModel && prediction.prompt_id && (
                              <Link
                                to={`/my-research/generate?prompt=${prediction.prompt_id}&from=${prediction.id}`}
                                className="btn btn-soft-primary btn-sm"
                              >
                                <i className="ri-add-line align-bottom me-1" />
                                Add a model
                              </Link>
                            )}
                            {/* A score nobody can check is a number the reader is
                                asked to trust. The page states the actual
                                weights. */}
                            <Link to="/how-scoring-works" className="fs-12">
                              How this is scored
                            </Link>
                          </div>

                        {/*
                          The same forecast as a TradingView indicator.

                          Members read these against a chart anyway, and drawing
                          the box by hand is the tedious part. It also reads back
                          the four numbers the settlement form asks for -- the
                          open, the high and low inside the window, and the close
                          -- because those have to be looked up somewhere and the
                          chart is where somebody is already looking.

                          Built in the browser: everything it needs is on this
                          page, and asking the server for a string would only add
                          a way for the button to fail.
                        */}
                        {!isCleanMode && pineScript && (
                          // Ruled off from the table above. The attribution line
                          // is a footer for the answers, and without a divider
                          // this heading read as part of it.
                          <div className="mt-4 pt-3 border-top mb-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <h5 className="text-dark mb-0">Draw on TradingView</h5>
                              <div className="d-flex gap-2">
                                {/* Copies whichever script is open. A single Copy
                                    that quietly copies the other one is a worse
                                    trap than two buttons. */}
                                <button type="button" className="btn btn-sm btn-light"
                                  disabled={!showPine}
                                  onClick={() => copyText(
                                    showPine === 'all' ? comparePineScript : pineScript,
                                    showPine === 'all' ? setComparePineCopied : setPineCopied,
                                  )}>
                                  {(showPine === 'all' ? comparePineCopied : pineCopied) ? 'Copied!' : 'Copy'}
                                </button>
                                <button type="button"
                                        className={`btn btn-sm ${showPine === 'one' ? 'btn-primary' : 'btn-soft-primary'}`}
                                        onClick={() => setShowPine((v) => (v === 'one' ? null : 'one'))}>
                                  This forecast
                                </button>
                                {/* Only when there is something to compare with. */}
                                {comparePineScript && (
                                  <button type="button"
                                          className={`btn btn-sm ${showPine === 'all' ? 'btn-primary' : 'btn-soft-primary'}`}
                                          onClick={() => setShowPine((v) => (v === 'all' ? null : 'all'))}>
                                    All {prediction.prompt_group.length} models
                                  </button>
                                )}
                              </div>
                            </div>
                            {showPine ? (
                              <>
                                <p className="text-muted fs-13 mb-2">
                                  Open the {prediction.symbol} chart on TradingView, paste this into
                                  the Pine editor and press <em>Add to chart</em>.{' '}
                                  {showPine === 'all'
                                    ? 'Each box is one model\u2019s range over the same window, in its'
                                      + ' own colour, and the table gives each as a move from the'
                                      + ' price that model found for itself.'
                                    : 'The box is the forecast range across its window, and the table'
                                      + ' reads back what price did inside it \u2014 including the four'
                                      + ' figures the settlement form asks for.'}
                                </p>
                                <pre className="p-3 bg-light rounded mb-0"
                                     style={{ maxHeight: 420, overflow: 'auto', whiteSpace: 'pre',
                                              wordBreak: 'normal', fontSize: 12, lineHeight: 1.5 }}>
                                  {showPine === 'all' ? comparePineScript : pineScript}
                                </pre>
                              </>
                            ) : (
                              <p className="text-muted fs-13 mb-0">
                                Pine Script indicators that plot this on a TradingView chart
                                {comparePineScript
                                  ? ' \u2014 this forecast on its own, or every model at once in'
                                    + ' different colours, which is the quickest way to see where'
                                    + ' they actually disagreed.'
                                  : ' \u2014 the window, the range, and what price did inside it.'}
                              </p>
                            )}
                          </div>
                        )}

                          {/* Grading, for the owner and for an admin.
                              Below the chart script on purpose: the script is
                              where the four numbers this form asks for come
                              from, so the order down the page is read them,
                              then enter them. */}
                          {canSettle && (
                            <div className="border-top mt-3 pt-3">
                              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                  <div className="fw-medium fs-13">
                                    This window has closed
                                  </div>
                                  <div className="text-muted fs-12">
                                    Nothing is scored until the price at the close is
                                    entered. Read it off the chart above.
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-warning"
                                  onClick={() => setSettling(true)}
                                >
                                  Grade it
                                </button>
                              </div>
                              <div className="text-muted fs-12 mt-2">
                                Settles all {settleSiblings.length || 1} unsettled answer
                                {(settleSiblings.length || 1) === 1 ? '' : 's'} to this question.
                              </div>
                            </div>
                          )}

                          {/* Publishing this question to Blogger, or
                              refreshing the post it already has. Administrator
                              only, and offered while the question is still
                              open: settling rewrites the same post rather than
                              adding a second one. */}
                          {canPublishBlogger && (
                            <div className="border-top mt-3 pt-3">
                              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                  <div className="fw-medium fs-13">
                                    {bloggerPostUrl ? 'Published to Blogger' : 'Not on Blogger yet'}
                                  </div>
                                  <div className="text-muted fs-12">
                                    {bloggerPostUrl ? (
                                      <>
                                        <a href={bloggerPostUrl} target="_blank" rel="noreferrer">
                                          View the post
                                        </a>
                                        {' '}— re-publish it if the score or reasoning has changed since.
                                      </>
                                    ) : (
                                      'Publish it now as an open forecast — it is rewritten with the outcome when this settles.'
                                    )}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  disabled={updatingBlogger}
                                  onClick={handleUpdateBlogger}
                                >
                                  {updatingBlogger
                                    ? (bloggerPostUrl ? 'Updating…' : 'Publishing…')
                                    : (bloggerPostUrl ? 'Update Blogger' : 'Publish to Blogger')}
                                </button>
                              </div>
                              {bloggerUpdateMsg && (
                                <div className={`text-${bloggerUpdateMsg.type} fs-12 mt-2`}>
                                  {bloggerUpdateMsg.text}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Sharing, for the owner and for an admin.
                              Under the table because it acts on the table: the
                              switch covers every model that answered this
                              question, not the row being viewed. Anyone else
                              never sees a private forecast at all, so there is
                              nothing here for them to be refused. */}
                          {canShare && (
                            <div className="border-top mt-3 pt-3">
                              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div>
                                  <div className="fw-medium fs-13">
                                    {isShared ? 'Shared publicly' : 'Private to you'}
                                  </div>
                                  <div className="text-muted fs-12">
                                    {isShared
                                      ? 'Anyone with the link can read this comparison, without signing in.'
                                      : 'Only you and an administrator can open this.'}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className={`btn btn-sm ${isShared ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                                  onClick={toggleShare}
                                  disabled={sharing}
                                >
                                  {sharing
                                    ? 'Saving…'
                                    : isShared ? 'Stop sharing' : 'Share publicly'}
                                </button>
                              </div>
                              {shareError && (
                                <div className="text-danger fs-12 mt-2">{shareError}</div>
                              )}
                              <div className="text-muted fs-12 mt-2">
                                Applies to all {prediction.prompt_group?.length || 1} answer
                                {(prediction.prompt_group?.length || 1) === 1 ? '' : 's'} to this question.
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {/* Reason Right (becomes bottom on mobile) - hide when clean mode without session param */}
                  {(!isCleanMode || (isCleanMode && showReasonParam)) && (
                    <div className="reason-section-mobile" style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      order: isMemberResearch ? 1 : 2,
                      // The divider belongs to whichever column is on the right.
                      ...(isMemberResearch
                        ? {}
                        : { borderLeft: '1px solid #eee', marginLeft: 32, paddingLeft: 32 }),
                    }}>
                      {isMemberResearch && !isCleanMode && (
                        <>
                          <PromptGroupTable
                            summaryOnly
                            group={prediction.prompt_group}
                            currentId={prediction.id}
                            baseline={prediction.current_price}
                            actualPrice={prediction.actual_price}
                            actualLow={prediction.actual_low}
                            actualHigh={prediction.actual_high}
                          />
                        </>
                      )}

                      {/* The prompt this forecast was made from, above the reasoning it
                          produced. Collapsed by default: a guided prompt runs to ~20KB.
                          Most older predictions predate the prompt archive, so the whole
                          section stays out of the way unless there is one to show. */}
                      {!isCleanMode && prediction.prompt_text && (
                        <div>
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            {/* The style badge is gone: members can only ask one
                                way now, so naming the style labelled a choice
                                that no longer exists. What it carried that was
                                worth keeping -- whether the prompt included
                                market data -- is said in the line below, where
                                it reads as a fact about this forecast rather
                                than as a category. */}
                            <h5 className="text-dark mb-0">Prompt</h5>
                            <div className="d-flex gap-2">
                              <button type="button" className="btn btn-sm btn-light"
                                onClick={() => copyText(prediction.prompt_text, setPromptCopied)}>
                                {promptCopied ? 'Copied!' : 'Copy'}
                              </button>
                              <button type="button" className="btn btn-sm btn-primary"
                                      onClick={() => setShowPrompt(v => !v)}>
                                {showPrompt ? 'Hide' : 'Show prompt'}
                              </button>
                            </div>
                          </div>
                          {showPrompt ? (
                            <pre className="p-3 bg-light rounded mb-0"
                                 style={{ maxHeight: 420, overflow: 'auto', whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word', fontSize: 12, lineHeight: 1.5 }}>
                              {prediction.prompt_text}
                            </pre>
                          ) : (
                            <p className="text-muted fs-13 mb-0">
                              {prediction.prompt_text.length.toLocaleString()} characters of{' '}
                              {/* A no-data prompt carries no market data, so saying it
                                  does describes the wrong thing entirely. */}
                              {prediction.prompt_style_carries_data === false
                                ? 'instructions this forecast was produced from — no market data was supplied, the model researched the asset itself.'
                                : 'market data and instructions this forecast was produced from.'}
                            </p>
                          )}
                        </div>
                      )}
                      <div className={`d-flex align-items-center justify-content-between mb-3${prediction.prompt_text && !isCleanMode ? ' mt-4' : ''}`}>
                        {/* Just the heading. Naming the model here as well was
                            noise: this panel is a teaser with a character count
                            and a button, and the attribution belongs with the
                            text it attributes -- which is /response, below. */}
                        <h5 className="text-dark mb-0">Reason</h5>
                        {/* Clean mode drives the screenshot pipeline, so it keeps the
                            plain rendering with no controls. */}
                        {!isCleanMode && (
                          <div className="d-flex gap-2 align-items-center">
                            {/* The reasoning is the part worth actually reading,
                                and here it is one collapsed panel among charts
                                and tables. This opens it on its own. */}
                            {prediction.reason && (
                              // On /response the reasoning is already open
                              // below, so the link is the way back rather than
                              // a link to the page you are on.
                              <Link
                                to={`/prediction/${predictionId}${isReasonMode ? '' : '/response'}`}
                                className="fs-13"
                              >
                                {isReasonMode ? 'Back to the chart' : 'Read in full'}
                              </Link>
                            )}
                            <button type="button" className="btn btn-sm btn-light"
                              disabled={!prediction.reason}
                              onClick={() => copyText(prediction.reason, setReasonCopied)}>
                              {reasonCopied ? 'Copied!' : 'Copy'}
                            </button>
                            <button type="button" className="btn btn-sm btn-primary"
                                    onClick={() => setShowReason(v => !v)}>
                              {showReason ? 'Hide' : 'Show reason'}
                            </button>
                          </div>
                        )}
                      </div>
                      {(isCleanMode || showReason) ? (
                        <div
                          className="reason-section p-3 bg-light rounded flex-grow-1 position-relative"
                          style={{
                            minHeight: 100,
                            maxHeight: (isCleanMode && showReasonParam) ? 'none' : (isExpanded ? 'none' : 420),
                            overflow: 'auto',
                            cursor: (isCleanMode && showReasonParam) ? 'default' : 'pointer',
                            whiteSpace: 'pre-wrap',
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                            lineHeight: 1.6
                          }}
                          onClick={!(isCleanMode && showReasonParam) ? (() => setIsExpanded(e => !e)) : undefined}
                          title={!(isCleanMode && showReasonParam) ? (isExpanded ? 'Click to collapse' : 'Click to expand') : undefined}
                        >
                          {prediction.reason || 'No reason available for this prediction.'}
                        </div>
                      ) : (
                        <p className="text-muted fs-13 mb-0">
                          {(prediction.reason || '').length.toLocaleString()} characters of model
                          reasoning behind this forecast.
                        </p>
                      )}

                    </div>
                  )}
                </div>
              </Col>
            </Row>
            )}

            {/* Backtest Mode - Table Section */}
            {isBacktest && backtestTrades.length > 0 && (
              <div className="mb-5 report-block" style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                {/* Backtest Options Panel */}
                <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '12px', color: '#333' }}>Backtest Parameters</div>
                  <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Cash Strategy Threshold</label>
                      <select
                        value={pendingCashStrategyParam}
                        onChange={e => setPendingCashStrategyParam(parseFloat(e.target.value))}
                        style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '13px', minWidth: '110px' }}
                      >
                        {Array.from({ length: 20 }, (_, i) => i + 1).map(v => (
                          <option key={v} value={v}>{(v / 10).toFixed(1)}%</option>
                        ))}
                      </select>
                      <div style={{ fontSize: '11px', color: '#999', marginTop: '3px' }}>cashstrategy={pendingCashStrategyParam}</div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Round Trip Fee</label>
                      <select
                        value={pendingRoundFeeParam}
                        onChange={e => setPendingRoundFeeParam(parseFloat(e.target.value))}
                        style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '13px', minWidth: '110px' }}
                      >
                        <option value={1}>0.1%</option>
                        <option value={2}>0.2%</option>
                        <option value={3}>0.3%</option>
                        <option value={5}>0.5%</option>
                        <option value={10}>1.0%</option>
                        <option value={20}>2.0%</option>
                      </select>
                      <div style={{ fontSize: '11px', color: '#999', marginTop: '3px' }}>roundfee={pendingRoundFeeParam}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="checkbox"
                          id="drawprice-cb"
                          checked={isDrawPrice}
                          onChange={e => {
                            setIsDrawPrice(e.target.checked);
                            try {
                              const sp = new URLSearchParams(window.location.search || '');
                              sp.set('drawprice', e.target.checked ? 'true' : 'false');
                              window.history.replaceState({}, '', `${window.location.pathname}?${sp.toString()}`);
                            } catch (e) { /* ignore */ }
                          }}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <label htmlFor="drawprice-cb" style={{ fontSize: '13px', color: '#333', cursor: 'pointer', userSelect: 'none' }}>Draw Price</label>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="checkbox"
                          id="showtable-cb"
                          checked={isShowTable}
                          onChange={e => {
                            setIsShowTable(e.target.checked);
                            try {
                              const sp = new URLSearchParams(window.location.search || '');
                              sp.set('showtable', e.target.checked ? 'true' : 'false');
                              window.history.replaceState({}, '', `${window.location.pathname}?${sp.toString()}`);
                            } catch (e) { /* ignore */ }
                          }}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <label htmlFor="showtable-cb" style={{ fontSize: '13px', color: '#333', cursor: 'pointer', userSelect: 'none' }}>Show Table</label>
                      </div>
                    </div>
                    <button
                      onClick={handleApplyBacktestParams}
                      style={{ padding: '7px 20px', backgroundColor: '#1976d2', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Apply
                    </button>
                  </div>
                  {(backtestCashStrategyParam !== pendingCashStrategyParam || backtestRoundFeeParam !== pendingRoundFeeParam) && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#e65100' }}>* Unsaved changes — click Apply to recalculate</div>
                  )}
                </div>
                {isShowTable && (
                <>
                <h4 className="text-dark mb-2">Backtest: Historical Trades for {prediction.symbol} using {prediction.model}</h4>
                <div style={{ fontSize: '13px', color: '#555', marginBottom: '20px', padding: '12px 14px', backgroundColor: '#e8f4fd', borderLeft: '3px solid #1976d2', borderRadius: '0 4px 4px 0', lineHeight: '1.7' }}>
                  <strong>Strategy:</strong> Each day's prediction midpoint is compared to the entry price.
                  {' '}<span style={{ color: '#2e7d32', fontWeight: 600 }}>LONG</span> when midpoint &gt; entry × (1 + {(backtestCashStrategyParam / 10).toFixed(1)}%) &nbsp;|&nbsp;
                  <span style={{ color: '#c62828', fontWeight: 600 }}>SHORT</span> when midpoint &lt; entry × (1 − {(backtestCashStrategyParam / 10).toFixed(1)}%) &nbsp;|&nbsp;
                  <span style={{ color: '#666', fontWeight: 600 }}>CASH</span> otherwise (within ±{(backtestCashStrategyParam / 10).toFixed(1)}% band).
                  {' '}A round-trip fee of <strong>{(backtestRoundFeeParam / 10).toFixed(1)}%</strong> is charged when entering a new position or flipping direction; holding the same position incurs no fee.
                  {' '}Cumulative return is compounded across all active (non-cash) trades.
                </div>
                <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#e9ecef', borderBottom: '2px solid #dee2e6' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Current Price</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Pred Low/High</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Actual Price</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Direction</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Suggestion</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Confidence</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Daily change</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Fee %</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>P/L %</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Cumulative P/L %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backtestTrades.map((trade, idx) => {
                        // Defensive rendering for 'No prediction' rows
                        const isNoPrediction = trade.id === 'no-prediction';
                        const cumulativePnL = trade.cumulativeReturn != null ? ((trade.cumulativeReturn - 1) * 100).toFixed(2) : '-';
                        // Daily change = (actual price - current price) / current price
                        const dailyChange = (trade.actualPrice != null && trade.currentPrice != null) 
                          ? ((trade.actualPrice - trade.currentPrice) / trade.currentPrice) 
                          : null;
                        const dailyChangePct = dailyChange != null ? (dailyChange * 100).toFixed(2) : '-';
                        const dailyPnL = trade.dailyReturn != null ? (trade.dailyReturn * 100).toFixed(2) : '-';
                        const isPositive = trade.pnl != null ? trade.pnl >= 0 : false;
                        const isNeutral = trade.isNeutral;

                        // Calculate suggestion for this trade
                        const tradeSuggestion = (() => {
                          if (isNoPrediction) return { label: 'NO PREDICTION', color: '#999' };
                          if (trade.predictionLow == null || trade.predictionHigh == null || trade.currentPrice == null) return { label: '-', color: '#999' };
                          const predictionMidpoint = (trade.predictionLow + trade.predictionHigh) / 2.0;
                          const changeRatio = (predictionMidpoint - trade.currentPrice) / trade.currentPrice;
                          const cashThres = backtestCashStrategyParam / 1000;
                          if (changeRatio > cashThres) {
                            return { label: 'LONG', color: '#2e7d32' };
                          } else if (changeRatio < -cashThres) {
                            return { label: 'SHORT', color: '#c62828' };
                          } else {
                            return { label: 'CASH', color: '#666' };
                          }
                        })();

                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: isNoPrediction ? '#ffecec' : (isNeutral ? '#f5f5f5' : (idx % 2 === 0 ? '#fff' : '#f8f9fa')), opacity: isNeutral || isNoPrediction ? 0.7 : 1 }}>
                            <td style={{ padding: '12px', textAlign: 'left', backgroundColor: isWeekend(trade.date) ? '#ffcccc' : 'transparent', color: isWeekend(trade.date) ? '#c62828' : 'inherit', fontWeight: isWeekend(trade.date) ? '600' : 'normal' }}>{trade.date}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {trade.currentPrice != null ? `$${trade.currentPrice.toFixed(2)}` : (isNoPrediction ? '-' : '-')}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {trade.predictionLow != null && trade.currentPrice != null ? formatPercentageChange(trade.predictionLow, trade.currentPrice) : '-'} / {trade.predictionHigh != null && trade.currentPrice != null ? formatPercentageChange(trade.predictionHigh, trade.currentPrice) : '-'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {trade.actualPrice != null ? `$${trade.actualPrice.toFixed(2)}` : (isNoPrediction ? '-' : '-')}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: isNoPrediction ? '#999' : (isNeutral ? '#999' : (trade.direction > 0 ? '#2e7d32' : '#c62828')) }}>
                              {isNoPrediction ? '○ NO PREDICTION' : (isNeutral ? '○ NEUTRAL' : (trade.direction > 0 ? '↑ LONG' : '↓ SHORT'))}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: tradeSuggestion.color }}>
                              {tradeSuggestion.label}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', fontFamily: 'monospace', color: '#333' }}>
                              {trade.confidence != null ? formatConfidence(trade.confidence) : (isNoPrediction ? '-' : '-')}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace', color: isNoPrediction ? '#999' : (dailyChange != null && dailyChange !== 0 ? (dailyChange > 0 ? '#2e7d32' : '#c62828') : '#999') }}>
                              {isNoPrediction ? '-' : (dailyChangePct !== '-' ? `${dailyChange >= 0 ? '+' : ''}${dailyChangePct}%` : '-')}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace', color: isNoPrediction ? '#999' : (isNeutral ? '#999' : '#666') }}>
                              {isNoPrediction ? '-' : (isNeutral ? '-' : (trade.feeCharged != null ? `${(trade.feeCharged * 100).toFixed(2)}%` : '-'))}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace', color: isNoPrediction ? '#999' : (isNeutral ? '#999' : (isPositive ? '#2e7d32' : '#c62828')), fontWeight: isNoPrediction ? 400 : (isNeutral ? 400 : 600) }}>
                              {isNoPrediction ? '-' : (isNeutral ? '-' : (dailyPnL != null ? `${isPositive ? '+' : ''}${dailyPnL}%` : '-'))}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', fontFamily: 'monospace', color: isNoPrediction ? '#999' : (cumulativePnL >= 0 ? '#2e7d32' : '#c62828'), fontWeight: isNoPrediction ? 400 : 600 }}>
                              {isNoPrediction ? '-' : (cumulativePnL != null ? (cumulativePnL >= 0 ? '+' : '') + cumulativePnL + '%' : '-')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginTop: '20px' }}>
                  {(() => {
                    // Only count non-neutral trades for statistics
                    const activeTrades = backtestTrades.filter(t => !t.isNeutral);
                    const winningTrades = activeTrades.filter(t => t.pnl >= 0).length;
                    const winRate = activeTrades.length > 0 ? ((winningTrades / activeTrades.length) * 100).toFixed(1) : 0;
                    const finalReturn = backtestTrades.length > 0 ? ((backtestTrades[backtestTrades.length - 1]?.cumulativeReturn - 1) * 100) : 0;
                    
                    // Calculate max drawdown
                    let maxDrawdown = 0;
                    if (backtestTrades.length > 0) {
                      let peak = backtestTrades[0]?.cumulativeReturn || 1;
                      for (const trade of backtestTrades) {
                        const current = trade.cumulativeReturn || 1;
                        if (current > peak) {
                          peak = current;
                        }
                        const drawdown = (current - peak) / peak;
                        if (drawdown < maxDrawdown) {
                          maxDrawdown = drawdown;
                        }
                      }
                    }
                    
                    return (
                      <>
                        <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Total Days</div>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>{backtestTrades.length}</div>
                        </div>
                        <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Total Trades</div>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>{activeTrades.length}</div>
                        </div>
                        <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Cash Days</div>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>{backtestTrades.length - activeTrades.length}</div>
                        </div>
                        <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Win Rate</div>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
                            {activeTrades.length > 0 ? `${winRate}%` : '-'}
                          </div>
                        </div>
                        <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Final Return</div>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: finalReturn >= 0 ? '#2e7d32' : '#c62828' }}>
                            {finalReturn >= 0 ? '+' : ''}{finalReturn.toFixed(2)}%
                          </div>
                        </div>
                        <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Max Drawdown</div>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: maxDrawdown < 0 ? '#c62828' : '#333' }}>
                            {(maxDrawdown * 100).toFixed(2)}%
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {isMemberResearch && !isCleanMode && <ResearchTiming timing={prediction.research_timing} answeredAt={prediction.model_answered_at} />}

            {/* Where the chart goes on the forecast page, and where the
                reasoning goes on /response. The two are alternatives rather
                than additions: this is the one wide, tall slot on the page, and
                the reasoning needs exactly that -- on the forecast page it is a
                collapsed panel in a column, capped at 420px, which is the
                reason this route exists. */}
            {isReasonMode ? (
              <div className="mb-5 report-block">
                {/* Attribution above the text it attributes. This page is given
                    over to one model's argument, so who wrote it and when they
                    say they wrote it are part of reading it -- and the version
                    matters, because the same catalogue entry is a different
                    model six months apart. */}
                <h5 className="text-dark mb-1">Reason</h5>
                <div className="mb-3">
                  <SelfReportLine
                    label={modelLabel}
                    modelId={String(prediction.model || '').replace(/^user:/, '')}
                    claimed={prediction.model_self_report}
                    answeredAt={prediction.model_answered_at}
                    askedAt={prediction.create_date}
                  />
                </div>
                {prediction.reason ? (
                  // Measured for reading rather than for fitting: 1.15rem over
                  // 1.8 line height, and pre-wrap so the model's own paragraphs
                  // and lists survive instead of collapsing into one block.
                  <div
                    className="bg-white border rounded p-4"
                    style={{
                      fontSize: '1.15rem',
                      lineHeight: 1.8,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {prediction.reason}
                  </div>
                ) : (
                  <div className="text-muted">
                    No reasoning was recorded with this forecast.
                  </div>
                )}
              </div>
            ) : (
            <div className="mb-5 report-block chart-wrap">
              <div style={{ height: isCleanMode && isVertical ? '1800px' : '420px', minHeight: isCleanMode && isVertical ? '1800px' : '420px' }}>
                  {showChart ? (
                    isBacktest ? (
                      // P/L Chart for Backtest Mode
                      (() => {
                        const chartHeight = isVertical ? Math.min(1600, (window?.innerHeight || 1600)) : 420;
                        const showPrice = isDrawPrice && backtestPriceData.length > 0;

                        // For hourly (and 4h) predictions, use category axis to avoid showing
                        // market-closed hours and weekend gaps between data points.
                        const isHourlyTf = prediction?.timeframe === '1h' || prediction?.timeframe === '4h';
                        const formatHourlyLabel = (ts) => {
                          // Correct for the 4-hour offset in the backend timestamp
                          const correctedTs = ts - (4 * 60 * 60 * 1000);
                          const d = new Date(correctedTs);
                          const year = d.getUTCFullYear();
                          const month = (d.getUTCMonth()+1).toString().padStart(2,'0');
                          const day = d.getUTCDate().toString().padStart(2,'0');
                          const hours = d.getUTCHours().toString().padStart(2,'0');
                          const minutes = d.getUTCMinutes().toString().padStart(2,'0');
                          return `${year}-${month}-${day} ${hours}:${minutes} UTC`;
                        };
                        const plChartData = isHourlyTf
                          ? backtestChartData.map(p => ({ x: formatHourlyLabel(p.x), y: p.y }))
                          : backtestChartData;
                        const priceChartDataForRender = isHourlyTf
                          ? backtestPriceData.map(p => ({ x: formatHourlyLabel(p.x), y: p.y }))
                          : backtestPriceData;

                        const backtestOptions = {
                          chart: {
                            type: 'line',
                            height: chartHeight,
                            toolbar: { show: true }
                          },
                          title: { text: `P/L Over Time - ${prediction.symbol} by ${prediction.model}`, align: 'left' },
                          xaxis: isHourlyTf ? {
                            type: 'category',
                            tickPlacement: 'on',
                            labels: { rotate: -45, rotateAlways: true, style: { fontSize: '10px' } }
                          } : {
                            type: 'datetime'
                          },
                          yaxis: showPrice ? [
                            {
                              seriesName: 'Cumulative P/L %',
                              title: { text: 'Cumulative P/L (%)' },
                              tooltip: { enabled: true },
                              labels: { formatter: (v) => v != null ? v.toFixed(2) + '%' : '' }
                            },
                            {
                              seriesName: `${prediction.symbol} Price`,
                              opposite: true,
                              title: { text: `${prediction.symbol} Price ($)` },
                              tooltip: { enabled: true },
                              labels: { formatter: (v) => v != null ? '$' + v.toFixed(2) : '' }
                            }
                          ] : {
                            title: { text: 'Cumulative P/L (%)' },
                            tooltip: { enabled: true }
                          },
                          dataLabels: { enabled: false },
                          stroke: { curve: 'straight', width: showPrice ? [3, 3] : 3 },
                          markers: { size: showPrice ? [5, 5] : 5 },
                          tooltip: {
                            shared: true,
                            theme: 'light',
                            custom: function({ series, seriesIndex, dataPointIndex, w }) {
                              // Get the actual x value (timestamp in milliseconds) from the data
                              const xVal = w.globals.initialSeries[0].data[dataPointIndex]?.x;
                              let dateLabel = '';
                              
                              if (typeof xVal === 'number') {
                                // The backend timestamp has a 4-hour offset, subtract it to get the correct time
                                const correctedTime = xVal - (4 * 60 * 60 * 1000);
                                const date = new Date(correctedTime);
                                const year = date.getUTCFullYear();
                                const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
                                const day = date.getUTCDate().toString().padStart(2, '0');
                                const hours = date.getUTCHours().toString().padStart(2, '0');
                                const minutes = date.getUTCMinutes().toString().padStart(2, '0');
                                dateLabel = `${year}-${month}-${day} ${hours}:${minutes} UTC`;
                              } else if (typeof xVal === 'string') {
                                // For hourly category data
                                dateLabel = xVal;
                              }
                              
                              let html = `<div style="padding:8px"><div><strong>Date:</strong> ${dateLabel}</div>`;
                              if (series[0] != null && series[0][dataPointIndex] != null) {
                                html += `<div style="color:#2e7d32"><strong>P/L:</strong> ${series[0][dataPointIndex].toFixed(2)}%</div>`;
                              }
                              if (showPrice && series[1] != null && series[1][dataPointIndex] != null) {
                                html += `<div style="color:#c62828"><strong>Price:</strong> $${series[1][dataPointIndex].toFixed(2)}</div>`;
                              }
                              html += '</div>';
                              return html;
                            }
                          },
                          fill: { opacity: 1 },
                          colors: showPrice ? ['#2e7d32', '#c62828'] : ['#2e7d32']
                        };

                        const backtestSeries = [{
                          name: 'Cumulative P/L %',
                          type: 'line',
                          data: plChartData
                        }];
                        if (showPrice) {
                          backtestSeries.push({
                            name: `${prediction.symbol} Price`,
                            type: 'line',
                            data: priceChartDataForRender
                          });
                        }

                        return (
                          <Chart
                            options={backtestOptions}
                            series={backtestSeries}
                            type="line"
                            height={chartHeight}
                          />
                        );
                      })()
                    ) : (isShowCandles && hasCandles) ? (
                      (() => {
                      // Build candlestick series for this prediction
                      const toNum = (v) => { const n = parseFloat(v); return (n == null || isNaN(n)) ? null : n; };
                      const predLow = toNum(prediction.prediction_low);
                      const predHigh = toNum(prediction.prediction_high);

                      let seriesData = [];

                      // If the backend returned a 'candles' array, use it for detailed OHLC plotting
                      if (prediction.candles && Array.isArray(prediction.candles) && prediction.candles.length > 1) {
                        seriesData = prediction.candles.map(c => {
                          // support multiple timestamp field names
                          const timestamp = c.timestamp ? new Date(c.timestamp).getTime() : (c.time ? (new Date(c.time * 1000).getTime()) : (c.t ? (new Date(c.t * 1000).getTime()) : Date.now()));
                          const open = toNum(c.open ?? c.o ?? c.o ?? c.o); // support multiple key names
                          const high = toNum(c.high ?? c.h);
                          const low = toNum(c.low ?? c.l);
                          const close = toNum(c.close ?? c.c);
                          const obj = { x: timestamp, y: [open ?? low ?? close ?? 0, high ?? close ?? 0, low ?? open ?? close ?? 0, close ?? open ?? low ?? 0] };
                          // carry through score if present
                          if (c.score != null) obj.score = Number(c.score);
                          if (c.model) obj.model = c.model;
                          return obj;
                        });
                      } else {
                        // Fallback: single prediction candle
                        const actual = toNum(prediction.actual_price ?? prediction.current_price ?? prediction.price);
                        const close = (actual !== null && actual !== undefined) ? actual : ((predLow !== null && predHigh !== null) ? ((predLow + predHigh) / 2) : 0);
                        const ts = prediction.create_date ? (new Date(prediction.create_date).getTime()) : Date.now();
                        seriesData = [{ x: ts, y: [predLow ?? close, predHigh ?? close, predLow ?? close, close], score: parseFloat(prediction.performance ?? prediction.score ?? 0) }];
                      }

                      /*
                       * Plot against an index axis rather than a time axis.
                       *
                       * With xaxis.type 'datetime', ApexCharts spaces candles by real
                       * elapsed time — so for an intraday series on a stock, the 17.5
                       * hours between one session's close and the next one's open
                       * become 17.5 hours of blank chart, and a weekend becomes 65.
                       * The candles arrive in clumps with nothing between them, which
                       * is what "the candles are not continuous" looks like.
                       *
                       * Trading platforms all solve this the same way: put the candles
                       * at even intervals and let the axis labels carry the real time.
                       * That is what this does. Every timestamp used as an x coordinate
                       * goes through toX() first, so closed-market time between two
                       * candles collapses to a single step no matter how long it was.
                       *
                       * Time we genuinely have no data for is NOT collapsed: a gap
                       * inside market hours, or a window running past the last candle,
                       * is extrapolated at the normal candle spacing and stays visible.
                       * Missing data should look missing.
                       */
                      seriesData = seriesData
                        .slice()
                        .sort((a, b) => (a.x || 0) - (b.x || 0));

                      const candleTimes = seriesData
                        .map(d => d.x)
                        .filter(x => typeof x === 'number' && !isNaN(x));

                      // Median rather than mean: the overnight gaps would drag an
                      // average far away from the real candle interval.
                      const candleStepMs = (() => {
                        if (candleTimes.length < 2) return 5 * 60 * 1000;
                        const diffs = [];
                        for (let i = 1; i < candleTimes.length; i += 1) {
                          const d = candleTimes[i] - candleTimes[i - 1];
                          if (d > 0) diffs.push(d);
                        }
                        if (!diffs.length) return 5 * 60 * 1000;
                        diffs.sort((a, b) => a - b);
                        return diffs[Math.floor(diffs.length / 2)];
                      })();

                      /** A real timestamp, placed on the index axis. May fall outside the data. */
                      const toX = (ms) => {
                        if (ms == null || isNaN(ms)) return null;
                        if (!candleTimes.length) return 0;
                        const first = candleTimes[0];
                        const last = candleTimes[candleTimes.length - 1];
                        if (ms <= first) return (ms - first) / candleStepMs;
                        if (ms >= last) return (candleTimes.length - 1) + (ms - last) / candleStepMs;
                        let lo = 0;
                        let hi = candleTimes.length - 1;
                        while (hi - lo > 1) {
                          const mid = (lo + hi) >> 1;
                          if (candleTimes[mid] <= ms) lo = mid; else hi = mid;
                        }
                        const span = candleTimes[hi] - candleTimes[lo];
                        return lo + (span > 0 ? (ms - candleTimes[lo]) / span : 0);
                      };

                      /** An index-axis position back to a real timestamp, for labels. */
                      const fromX = (idx) => {
                        if (!candleTimes.length) return null;
                        const i = Math.round(Number(idx));
                        if (i < 0) return candleTimes[0] + i * candleStepMs;
                        if (i >= candleTimes.length) {
                          return candleTimes[candleTimes.length - 1] + (i - (candleTimes.length - 1)) * candleStepMs;
                        }
                        return candleTimes[i];
                      };

                      // Keep the real timestamp on each point so the tooltip can show
                      // it — the axis no longer carries it implicitly.
                      seriesData = seriesData.map((d, i) => ({ ...d, x: i, t: candleTimes[i] }));

                      const series = [{ name: prediction.model || 'Prediction', data: seriesData }];

                      // Compute chart vertical bounds so the prediction box occupies
                      // 50% of the chart height (i.e., y-axis span = 2x prediction range)
                      // with 1/4 spacing above and below. If prediction low/high are
                      // available, we use them to compute min/max. If prediction range
                      // is unavailable, fall back to series extents with a small pad.
                      let yMin = null, yMax = null;
                      // Measure series extents (if present) so we can fall back
                      const seriesLows = seriesData.map(d => Array.isArray(d.y) ? Number(d.y[2]) : null).filter(v => v !== null && !isNaN(v));
                      const seriesHighs = seriesData.map(d => Array.isArray(d.y) ? Number(d.y[1]) : null).filter(v => v !== null && !isNaN(v));
                      const seriesMin = seriesLows.length ? Math.min(...seriesLows) : null;
                      const seriesMax = seriesHighs.length ? Math.max(...seriesHighs) : null;

                      if (predLow != null && predHigh != null && !isNaN(predLow) && !isNaN(predHigh)) {
                        // Prediction range-based sizing
                        const range = Number(predHigh) - Number(predLow);
                        // If prediction range is zero or extremely small, fall back to small absolute padding
                        const safeRange = (range && range > 0) ? range : (Math.abs(Number(predHigh)) * 0.02 || 1);
                        const desiredSpan = safeRange * 2; // y-axis span = 2x prediction range
                        const center = (Number(predHigh) + Number(predLow)) / 2;
                        yMin = center - (desiredSpan / 2);
                        yMax = center + (desiredSpan / 2);
                        // If series extends beyond this desired span, expand to include series
                        if (seriesMin != null && seriesMin < yMin) yMin = Math.min(yMin, seriesMin - Math.abs(seriesMin) * 0.01);
                        if (seriesMax != null && seriesMax > yMax) yMax = Math.max(yMax, seriesMax + Math.abs(seriesMax) * 0.01);
                      } else if (seriesMin != null && seriesMax != null) {
                        // Fallback: use series extents with a small 2% pad
                        const pad = (seriesMax - seriesMin) * 0.02 || (Math.abs(seriesMax) * 0.02 || 1);
                        yMin = seriesMin - pad;
                        yMax = seriesMax + pad;
                      }

                      const predStartMs = prediction.create_date ? new Date(prediction.create_date).getTime() : null;
                      const predEndMs = prediction.target_date ? new Date(prediction.target_date).getTime() : null;
                      const formatUtc = (ms) => ms ? (new Date(ms).toISOString().replace('T',' ').slice(0,16) + ' UTC') : '';
                      const formatPrice = (v) => {
                        if (v == null || isNaN(Number(v))) return 'N/A';
                        // Choose decimals: if price < 1 use 6 decimals, else 2
                        const n = Number(v);
                        return (Math.abs(n) < 1) ? n.toFixed(6) : n.toFixed(2);
                      };

                      // Build shapes array: main rectangle (time+price) and small
                      // Start/End markers occupying ~10% of the displayed x-range
                      const shapesArr = [];
                      // compute x-range from series data when available
                      let xMin = null, xMax = null;
                      if (Array.isArray(seriesData) && seriesData.length) {
                        // seriesData.x is now an index, so these bounds are too.
                        const xs = seriesData.map(d => d.x).filter(x => typeof x === 'number');
                        if (xs.length) { xMin = Math.min(...xs); xMax = Math.max(...xs); }
                      }
                      // Expand x-range to include the prediction window so
                      // Start/End small boxes are sized relative to the
                      // visible range and centered correctly.
                      const predStartX = toX(predStartMs);
                      const predEndX = toX(predEndMs);
                      if (predStartX != null && predEndX != null) {
                        if (xMin == null || predStartX < xMin) xMin = predStartX;
                        if (xMax == null || predEndX > xMax) xMax = predEndX;
                      }
                      // If we have a price band rectangle, add it first
                      if (predStartMs && predEndMs && predEndMs > predStartMs && predLow != null && predHigh != null && predHigh > predLow) {
                        shapesArr.push({
                          type: 'rect', x: predStartX, x2: predEndX, y: predLow, y2: predHigh,
                          fillColor: 'rgba(0,41,102,0.38)', strokeColor: 'rgba(0,12,50,0.98)', strokeWidth: 3, opacity: 0.98
                        });
                      }

                      // small start/end markers: use 10% of the x-range (fallback to 1 hour if unknown)
                      if (predStartMs && predEndMs && predEndMs > predStartMs) {
                        const fallbackWidth = 1; // one candle wide
                        // Use the combined x-range (series + prediction window)
                        // to compute box width as 10% of the currently visible
                        // chart width. Fall back to prediction duration or 1 hour.
                        const xRange = (xMin != null && xMax != null) ? Math.max(1, xMax - xMin) : Math.max(1, (predEndX ?? 1) - (predStartX ?? 0));
                        const boxWidth = Math.max(xRange * 0.10, fallbackWidth);
                        // compute vertical middle band that occupies ~50% of y range
                        let centerY = null, halfH = null;
                        if (yMin != null && yMax != null && yMax > yMin) {
                          centerY = (yMin + yMax) / 2;
                          halfH = (yMax - yMin) * 0.25;
                        }
                        const yTop = centerY != null ? centerY + halfH : (predHigh != null ? predHigh : (yMax || 0));
                        const yBottom = centerY != null ? centerY - halfH : (predLow != null ? predLow : (yMin || 0));
                        // Start box centered at predStartMs
                        shapesArr.push({ type: 'rect', x: predStartX - (boxWidth/2), x2: predStartX + (boxWidth/2), y: yBottom, y2: yTop, fillColor: 'rgba(0,41,102,0.65)', strokeColor: 'rgba(0,12,50,1)', strokeWidth: 2, opacity: 0.95 });
                        // End box centered at predEndMs
                        shapesArr.push({ type: 'rect', x: predEndX - (boxWidth/2), x2: predEndX + (boxWidth/2), y: yBottom, y2: yTop, fillColor: 'rgba(0,41,102,0.65)', strokeColor: 'rgba(0,12,50,1)', strokeWidth: 2, opacity: 0.95 });
                      }

                      // Compute timeframe (ms) from prediction.timeframe so we
                      // can set an exact margin of 5 * timeframe before/after
                      // the prediction window. Fallback to 1 hour when unknown.
                      const parseTimeframeToMs = (tf) => {
                        if (!tf) return 60 * 60 * 1000;
                        const s = tf.toString().toLowerCase().trim();
                        if (s.endsWith('m')) {
                          const n = parseFloat(s.replace(/[^0-9.]/g, '')) || 15;
                          return n * 60 * 1000;
                        }
                        if (s.endsWith('h')) {
                          const n = parseFloat(s.replace(/[^0-9.]/g, '')) || 1;
                          return n * 60 * 60 * 1000;
                        }
                        if (s.endsWith('d')) {
                          const n = parseFloat(s.replace(/[^0-9.]/g, '')) || 1;
                          return n * 24 * 60 * 60 * 1000;
                        }
                        // fallback
                        return 60 * 60 * 1000;
                      };

                      const tfMs = parseTimeframeToMs(prediction?.timeframe);

                      // If we have both predStart and predEnd, prefer to set the
                      // x-axis to start = predStart - 5*tf and end = predEnd + 5*tf
                      // so the prediction window is centered with margins. If
                      // predStart/End missing, fall back to combined series range.
                      let xAxisMin, xAxisMax;
                      if (predStartX != null && predEndX != null) {
                        /*
                         * Frame everything we have: all the candles the backend chose to
                         * send, plus the forecast window, plus a little air.
                         *
                         * The margin used to be "five forecast periods either side",
                         * which was proportionate on a time axis. On an index axis it is
                         * not: five days of wall clock is 1,440 five-minute steps, but
                         * only about 390 of those are candles that exist, so converting
                         * the duration into steps zoomed out by roughly a factor of ten
                         * and squeezed the whole series into a sliver. Bounding by the
                         * data itself has no unit to get wrong.
                         */
                        const dataMinX = 0;
                        const dataMaxX = Math.max(0, candleTimes.length - 1);
                        const lo = Math.min(dataMinX, predStartX);
                        const hi = Math.max(dataMaxX, predEndX);
                        const pad = Math.max(1, (hi - lo) * 0.03);
                        xAxisMin = lo - pad;
                        xAxisMax = hi + pad;
                        xMin = xAxisMin; xMax = xAxisMax;
                      } else {
                        const xPadding = (xMin != null && xMax != null) ? Math.max(1, (xMax - xMin) * 0.02) : 0;
                        xAxisMin = (xMin != null) ? (xMin - xPadding) : undefined;
                        xAxisMax = (xMax != null) ? (xMax + xPadding) : undefined;
                      }

                        const options = {
                          chart: { type: 'candlestick', height: isVertical ? Math.min(1600, (window?.innerHeight || 1600)) : 420, toolbar: { show: true } },
                          title: { text: `${prediction.symbol} prediction ${prediction.model ? `by ${prediction.model}` : ''}`, align: 'left' },
                          // Use UTC timestamps on x-axis to match the rest of the site
                          // (all pages are displayed using UTC time)
                          // Numeric index axis; the formatter puts the real time back
                          // on the labels so nothing is lost by not spacing by time.
                          xaxis: {
                            type: 'numeric',
                            min: xAxisMin,
                            max: xAxisMax,
                            tickAmount: 6,
                            labels: {
                              rotate: 0,
                              formatter: (val) => {
                                const ms = fromX(val);
                                return ms ? new Date(ms).toISOString().replace('T', ' ').slice(5, 16) : '';
                              },
                            },
                          },
                          // Fix the vertical bounds to ensure the prediction timeframe
                          // box is fully visible and not clipped by the chart autoscale.
                          yaxis: { min: yMin, max: yMax, tooltip: { enabled: true } },
                          dataLabels: { enabled: false },
                          tooltip: {
                            shared: true,
                            custom: function({ series, seriesIndex, dataPointIndex, w }){
                              const s = w.globals.initialSeries?.[seriesIndex]?.data?.[dataPointIndex];
                              const score = (s && s.score) ? Number(s.score).toFixed(2) : 'N/A';
                              const when = s && s.t ? new Date(s.t).toISOString().replace('T', ' ').slice(0, 16) + ' UTC' : '';
                              return `<div style="padding:8px">${when ? `<div><strong>${when}</strong></div>` : ''}<div><strong>Model:</strong> ${s.model || prediction.model || 'N/A'}</div><div><strong>Pred Low:</strong> ${s.y[2]}</div><div><strong>Pred High:</strong> ${s.y[1]}</div><div><strong>Close/Actual:</strong> ${s.y[3]}</div><div><strong>Score:</strong> ${score}</div></div>`;
                            }
                          },
                          annotations: {
                              // shade predicted range if available (price band)
                              // Use a much darker blue for the predicted price band to
                              // improve visibility on the chart
                              // Also add two price labels at the top/bottom of the box
                              yaxis: (() => {
                                if (!(predLow != null && predHigh != null)) return [];
                                const arr = [];
                                // main shaded box
                                arr.push({ y: predLow, y2: predHigh, borderColor: 'transparent', fillColor: 'rgba(0,41,102,0.18)' });
                                // top label (Pred High) — anchor left and offset further right
                                // so the label sits to the right of the y-axis without overlapping
                                // nudged 10% further right (48 -> 53)
                                arr.push({ y: predHigh, borderColor: 'rgba(0,12,50,0.9)', label: { text: `${formatPrice(predHigh)}`, style: { color: '#fff', background: 'rgba(0,12,50,0.95)', fontSize: '12px' }, position: 'left', offsetX: 53, offsetY: -6 } });
                                // bottom label (Pred Low) — anchor left and offset further right
                                // so the label sits to the right of the y-axis without overlapping
                                // nudged 10% further right (48 -> 53)
                                arr.push({ y: predLow, borderColor: 'rgba(0,12,50,0.9)', label: { text: `${formatPrice(predLow)}`, style: { color: '#fff', background: 'rgba(0,12,50,0.95)', fontSize: '12px' }, position: 'left', offsetX: 53, offsetY: 6 } });
                                return arr;
                              })(),
                            // Draw a vertical band when we don't have both price
                            // bounds. If we DO have both time and price bounds we
                            // prefer to draw a precise rectangle (below) instead
                            // of a full-height xaxis band.
                            // Clearer/darker blue vertical band fallback when price bounds missing
                            // Build the xaxis annotation array. We add: optional
                            // full-height band (fallback), plus two point annotations
                            // with labels for start and end timestamps.
                            xaxis: (() => {
                              const arr = [];
                              if (predStartX != null && predEndX != null && predEndX > predStartX && !(predLow != null && predHigh != null && predHigh > predLow)) {
                                arr.push({ x: predStartX, x2: predEndX, borderColor: 'rgba(0,41,102,0.85)', fillColor: 'rgba(0,41,102,0.12)' });
                              }
                              if (predStartX != null) {
                                arr.push({ x: predStartX, borderColor: 'rgba(0,12,50,0.9)', label: { text: 'Start', orientation: 'horizontal', position: 'top', style: { color: '#fff', background: 'rgba(0,12,50,0.95)', fontSize: '12px' } } });
                              }
                              if (predEndX != null) {
                                arr.push({ x: predEndX, borderColor: 'rgba(0,12,50,0.9)', label: { text: 'End', orientation: 'horizontal', position: 'top', style: { color: '#fff', background: 'rgba(0,12,50,0.95)', fontSize: '12px' } } });
                              }
                              return arr;
                            })(),
                            // Draw a precise rectangle limited to the time and
                            // predicted price band, plus the derived Start/End
                            // small boxes computed above.
                            shapes: shapesArr
                          }
                        };

                        return (
                          <Chart options={options} series={series} type="candlestick" height={isVertical ? Math.min(1600, (window?.innerHeight || 1600)) : 420} />
                        );
                      })()
                    ) : (
                      <TradingViewChart
                        symbol={tvSymbol}
                        interval={tvInterval}
                        autosize={true}
                        theme="dark"
                        height={isCleanMode && isVertical ? 1600 : 420}
                      />
                    )
                  ) : (
                    <div className="text-muted" style={{ paddingTop: 12 }}>Loading chart…</div>
                  )}
              </div>
            </div>
            )}
          </div>
        </Container>

        {/* The same dialog the research list uses. Kept out of the column
            layout above because it is a modal, and mounted only once there is
            something to settle so it never has to guard against a null row. */}
        {settleTarget && (
          <SettleModal
            isOpen={settling}
            toggle={() => setSettling(false)}
            prediction={settleTarget}
            siblings={settleSiblings}
            onSettled={() => setReloadKey((k) => k + 1)}
          />
        )}
      </div>
    </>
  );
}
